import { loadSwiper } from './swiper-loader.js';
import { PopupModal } from './popup-modal.js';
import { Filter } from 'https://cdn.jsdelivr.net/npm/profanity-check@0.0.3/dist/profanity-check.esm.js';

const profanityFilter = new Filter();

const encodeAttr = (value) => encodeURIComponent(JSON.stringify(value));

const decodeAttr = (value) => JSON.parse(decodeURIComponent(value));

const PERSONALIZATION_CONFIG = {
    fileNameSource: 'title', // 'title' | 'metafield'
    uvPrintingTitleMatch: 'UV Printing',
};

const optionValue = (option) => option?.id ?? option?.label;

const getFileName = (baseProduct) => {
    if (!baseProduct) {
        return null;
    }

    if (PERSONALIZATION_CONFIG.fileNameSource === 'metafield') {
        return baseProduct.file_name || null;
    }

    return baseProduct.title || null;
};

const isUvPrintingType = (type) => {
    const title = type?.properties?.product?.title ?? '';

    return title.toLowerCase().includes(PERSONALIZATION_CONFIG.uvPrintingTitleMatch.toLowerCase());
};

const formatPxToRem = (value) => {
    if (value == null || value === '') {
        return null;
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue === 0) {
        return null;
    }

    return `calc(${numericValue} / 16 * 1rem)`;
};

const optionGroupFingerprint = (step, options) => {
    if (!step || !options?.length) {
        return null;
    }

    const values = options.map((option) => optionValue(option)).join('\0');
    return `${step}:${values}`;
};

const getSharedOptionGroupFingerprints = (currentFingerprints, destinationFingerprints) => {
    const destinationSet = new Set(destinationFingerprints);
    return currentFingerprints.filter((fingerprint) => destinationSet.has(fingerprint));
};

const findImageOption = (type, predicate, categoryIndex = null) => {
    const numericCategoryIndex = Number(categoryIndex);

    if (
        Number.isFinite(numericCategoryIndex) &&
        type?.categories?.[numericCategoryIndex]
    ) {
        const scopedOption = type.categories[numericCategoryIndex].options?.find(predicate);

        if (scopedOption) {
            return scopedOption;
        }
    }

    for (const category of type?.categories ?? []) {
        const option = category.options?.find(predicate);

        if (option) {
            return option;
        }
    }

    return null;
};

// Shared mutable wizard state and draft persistence.
class PersonalizationState {
    constructor(data) {
        this.data = data;
        this.confirmedPersonalizations = [];
        this.currentSelections = {
            product: data.personalizations[0],
            placement: null,
            type: null,
            details: null,
        };
        this.currentPersonalizationProduct = null;
        this.editingConfirmedIndex = null;
        this.steps = { current: 1, total: 4 };
        this.currentCartItems = null;
        this.cartEditGroupKeys = null;
        this.cartEditQuantities = null;
    }

    getConfirmedDraftStorageKey() {
        return `calpak_personalization_${this.data.base_product.id}`;
    }

    persistDraft() {
        const key = this.getConfirmedDraftStorageKey();

        if (!this.confirmedPersonalizations?.length) {
            this.clearDraft();
            return;
        }

        if (typeof window.setStorageItem === 'function') {
            window.setStorageItem(key, { confirmedPersonalizations: this.confirmedPersonalizations }, 7);
        }
    }

    loadDraft() {
        if (typeof window.expireStorageItem !== 'function') {
            return;
        }

        const value = window.expireStorageItem(this.getConfirmedDraftStorageKey());

        if (!value?.confirmedPersonalizations?.length) {
            return;
        }

        this.confirmedPersonalizations = value.confirmedPersonalizations;
        this.editingConfirmedIndex = null;
        this.renumberLocationKeys();
        PersonalizationCart.syncFromState(this, { includeCurrentDraft: false });
    }

    clearDraft() {
        const key = this.getConfirmedDraftStorageKey();

        if (typeof window.storageAvailable === 'function' && window.storageAvailable()) {
            localStorage.removeItem(key);
        }
    }

    resetAll() {
        this.confirmedPersonalizations = [];
        this.currentSelections = {
            product: this.data.personalizations[0],
            placement: null,
            type: null,
            details: null,
        };
        this.currentPersonalizationProduct = null;
        this.editingConfirmedIndex = null;
        this.steps.current = 1;
        this.cartEditGroupKeys = null;
        this.cartEditQuantities = null;
        PersonalizationCart.syncFromState(this, { includeCurrentDraft: false });
    }

    resetInProgress() {
        this.currentSelections = {
            product: this.currentSelections.product,
            placement: null,
            type: null,
            details: null,
        };
        this.currentPersonalizationProduct = null;
        this.editingConfirmedIndex = null;
        this.steps.current = 1;
        PersonalizationCart.syncFromState(this, { includeCurrentDraft: false });
    }

    renumberLocationKeys() {
        this.confirmedPersonalizations.forEach((entry, index) => {
            entry.locationKey = `Location_${index + 1}`;
        });
    }

    buildConfirmedEntry() {
        const selections = {
            product: this.currentSelections.product,
            placement: this.currentSelections.placement,
            type: this.currentSelections.type,
            details: { ...this.currentSelections.details },
        };

        const locationKey =
            this.editingConfirmedIndex !== null
                ? this.confirmedPersonalizations[this.editingConfirmedIndex].locationKey
                : `Location_${this.confirmedPersonalizations.length + 1}`;

        return {
            locationKey,
            productLabel: selections.product?.label ?? null,
            placementLabel: selections.placement?.label,
            selections,
            personalizationVariantId: this.currentPersonalizationProduct.id,
            price: this.currentPersonalizationProduct.price,
            basePropertyValue: PersonalizationCart.formatBaseProperties(
                selections,
                selections.details,
                getFileName(this.data.base_product)
            ),
            personalizationProperties: PersonalizationCart.formatPersonalizationProperties(
                selections,
                selections.details,
                getFileName(this.data.base_product)
            ),
        };
    }

    hasUnconfiguredPlacements(product = this.currentSelections.product) {
        return product.placements.some(
            (placement) => !PersonalizationStepPlacement.isPlacementConfigured(this, placement, product)
        );
    }

    hasAnyUnconfiguredPlacements() {
        return this.data.personalizations.some((product) => this.hasUnconfiguredPlacements(product));
    }

    getFirstProductWithUnconfiguredPlacements() {
        return (
            this.data.personalizations.find((product) => this.hasUnconfiguredPlacements(product)) ??
            this.data.personalizations[0]
        );
    }

    commitCurrentDraft() {
        const entry = this.buildConfirmedEntry();

        if (this.editingConfirmedIndex !== null) {
            this.confirmedPersonalizations[this.editingConfirmedIndex] = entry;
            this.editingConfirmedIndex = null;
        } else {
            this.confirmedPersonalizations.push(entry);
        }

        this.renumberLocationKeys();
    }
}

class PersonalizationCart extends HTMLElement {
    static CART_EDIT_STORAGE_KEY = 'calpak_personalization_cart_edit';

    static formatBaseProperties(selections, details, fileName = null) {
        const filePrefix = fileName && isUvPrintingType(selections.type) ? `File Name: ${fileName} | ` : '';
        const productPrefix = selections.product?.label ? `Product: ${selections.product.label} | ` : '';
        let baseProperties = `${filePrefix}${productPrefix}Location: ${selections.placement?.label} | Type: ${selections.type?.label}`;

        if (selections.type?.type == 'text') {
            baseProperties += ` | ${details?.text ? `Characters: ${details.text} | ` : ''}${details?.icon ? `Icon: ${details.icon} | ` : ''}${details?.font ? `Font: ${details.font.name} | ` : ''}${details?.color ? `Color: ${details.color.name} - ${details.color.thread}` : ''}`;
        } else {
            const detailKey = selections.type?.label.toLowerCase();
            baseProperties += ` | ${selections.type?.label}: ${details?.[detailKey]?.label ?? ''}`;
        }

        return baseProperties;
    }

    static formatPersonalizationProperties(selections, details, fileName = null) {
        const productProperty = selections.product?.label ? { product: selections.product.label } : {};
        const fileNameProperty = fileName && isUvPrintingType(selections.type) ? { 'File Name': fileName } : {};

        if (selections.type?.type == 'text') {
            return {
                location: selections.placement?.label,
                ...productProperty,
                ...fileNameProperty,
                ...(details?.text ? { Initials: details.text } : {}),
                ...(details?.icon ? { Icon: details.icon } : {}),
                ...(details?.font ? { Font: details.font.name } : {}),
                ...(details?.color ? { Color: details.color.name } : {}),
            };
        }

        return {
            location: selections.placement?.label,
            ...productProperty,
            ...fileNameProperty,
        };
    }

    static formatConfirmationSummary(selections) {
        const { product, placement, type, details } = selections;
        const productPrefix = product?.label ? `${product.label} – ` : '';
        const label = `${productPrefix}${placement?.label} – ${type?.label}`;

        if (type?.type == 'text') {
            const detailParts = [
                details?.text ? `"${details.text}"` : '',
                details?.icon ?? '',
                details?.font?.name ?? '',
                details?.color?.name ?? '',
            ].filter(Boolean);

            return `${label} – ${detailParts.join(' – ')}`;
        }

        return `${label} – ${details?.[type?.label.toLowerCase()]?.label ?? ''}`;
    }

    static shouldShowConfirmationDraft(state) {
        return (
            state.steps.current === state.steps.total &&
            PersonalizationStepDetails.isDraftComplete(state) &&
            !PersonalizationStepPlacement.isCurrentPlacementConfirmed(state) &&
            state.currentPersonalizationProduct?.price
        );
    }

    static getConfirmationPricing(state) {
        let personalizationTotal = 0;
        const rows = [];
        const showCurrentDraft = PersonalizationCart.shouldShowConfirmationDraft(state);

        for (const [index, entry] of state.confirmedPersonalizations.entries()) {
            if (showCurrentDraft && state.editingConfirmedIndex === index) {
                continue;
            }

            personalizationTotal += entry.price;
            rows.push({
                summary: PersonalizationCart.formatConfirmationSummary(entry.selections),
                price: entry.price,
                entryIndex: index,
                isDraft: false,
            });
        }

        if (showCurrentDraft) {
            personalizationTotal += state.currentPersonalizationProduct.price;
            rows.push({
                summary: PersonalizationCart.formatConfirmationSummary(state.currentSelections),
                price: state.currentPersonalizationProduct.price,
                entryIndex: state.editingConfirmedIndex,
                isDraft: true,
            });
        }

        return {
            rows,
            subtotal: state.data.base_product.price + personalizationTotal,
        };
    }

    static syncFromState(state, { includeCurrentDraft = false } = {}) {
        state.renumberLocationKeys();

        const groupId = crypto.randomUUID();

        const baseProperties = {
            '_final-sale': true,
            '_LPROP': 'Final Sale',
            '_personalization_id': groupId,
            ...(state.data?.base_product?.preorder ? { 'Pre-Order': state.data.base_product.preorder } : {}),
            ...(typeof window.compareAtPriceProperties === 'function'
                ? window.compareAtPriceProperties(
                    state.data?.base_product?.compare_at_price,
                    state.data?.base_product?.price,
                )
                : {}),
        };

        const feeProperties = (properties) => ({
            '_final-sale': true,
            '_LPROP': 'Final Sale',
            '_personalization_id': groupId,
            '_personalization_fee': true,
            ...properties,
        });

        const personalizationProducts = [];
        const fileName = getFileName(state.data.base_product);
        const useDraftOverlay =
            includeCurrentDraft &&
            PersonalizationStepDetails.isDraftComplete(state) &&
            !PersonalizationStepPlacement.isCurrentPlacementConfirmed(state) &&
            state.currentPersonalizationProduct?.id;

        for (const [index, entry] of state.confirmedPersonalizations.entries()) {
            if (useDraftOverlay && state.editingConfirmedIndex === index) {
                baseProperties[entry.locationKey] = PersonalizationCart.formatBaseProperties(
                    state.currentSelections,
                    state.currentSelections.details,
                    fileName
                );

                personalizationProducts.push({
                    id: state.currentPersonalizationProduct.id,
                    quantity: 1,
                    properties: feeProperties(
                        PersonalizationCart.formatPersonalizationProperties(
                            state.currentSelections,
                            state.currentSelections.details,
                            fileName
                        )
                    ),
                });
            } else {
                baseProperties[entry.locationKey] = entry.basePropertyValue;

                personalizationProducts.push({
                    id: entry.personalizationVariantId,
                    quantity: 1,
                    properties: feeProperties(entry.personalizationProperties),
                });
            }
        }

        if (useDraftOverlay && state.editingConfirmedIndex === null) {
            const draftKey = `Location_${state.confirmedPersonalizations.length + 1}`;
            baseProperties[draftKey] = PersonalizationCart.formatBaseProperties(
                state.currentSelections,
                state.currentSelections.details,
                fileName
            );

            personalizationProducts.push({
                id: state.currentPersonalizationProduct.id,
                quantity: 1,
                properties: feeProperties(
                    PersonalizationCart.formatPersonalizationProperties(
                        state.currentSelections,
                        state.currentSelections.details,
                        fileName
                    )
                ),
            });
        }

        state.currentCartItems = {
            baseProduct: {
                id: state.data.base_product.id,
                quantity: 1,
                properties: baseProperties,
            },
            personalizationProducts,
        };
    }

    static getLineItems(state) {
        const lineItems = [];
        const { baseProduct, personalizationProducts } = state.currentCartItems ?? {};

        if (baseProduct?.id) {
            lineItems.push({
                id: Number(baseProduct.id),
                quantity: baseProduct.quantity ?? 1,
                properties: baseProduct.properties ?? {},
            });
        }

        for (const item of personalizationProducts ?? []) {
            if (!item?.id) {
                continue;
            }

            lineItems.push({
                id: Number(item.id),
                quantity: item.quantity ?? 1,
                properties: item.properties ?? {},
            });
        }

        return lineItems;
    }

    static commitDraftIfComplete(state) {
        if (
            !PersonalizationStepDetails.isDraftComplete(state) ||
            PersonalizationStepPlacement.isCurrentPlacementConfirmed(state)
        ) {
            return;
        }

        state.commitCurrentDraft();
    }

    static canAddToCart(state) {
        return (
            state.confirmedPersonalizations.length > 0 ||
            (PersonalizationStepDetails.isDraftComplete(state) &&
                !PersonalizationStepPlacement.isCurrentPlacementConfirmed(state))
        );
    }

    static parseBasePropertyValue(value) {
        const parsed = {};

        if (!value) {
            return parsed;
        }

        for (const part of value.split(' | ')) {
            const colonIndex = part.indexOf(':');

            if (colonIndex === -1) {
                continue;
            }

            parsed[part.slice(0, colonIndex).trim()] = part.slice(colonIndex + 1).trim();
        }

        return parsed;
    }

    static findProductAndPlacement(data, locationLabel, productLabel = null) {
        const products = productLabel
            ? data.personalizations.filter((product) => product.label === productLabel)
            : data.personalizations;

        for (const product of products) {
            const placement = product.placements.find((entry) => entry.label === locationLabel);

            if (placement) {
                return { product, placement };
            }
        }

        if (productLabel) {
            return PersonalizationCart.findProductAndPlacement(data, locationLabel);
        }

        return null;
    }

    static findFeeForLocation(feeItems, locationLabel, productLabel, usedFeeKeys) {
        return feeItems.find(
            (entry) =>
                !usedFeeKeys.has(entry.key) &&
                entry.properties?.location === locationLabel &&
                (productLabel == null ||
                    entry.properties?.product === productLabel ||
                    !entry.properties?.product)
        );
    }

    static buildDetailsFromCart(type, parsed, fee) {
        if (type.type === 'text') {
            const details = {};
            const text = fee?.properties?.Initials ?? parsed.Characters;

            if (text) {
                details.text = text;
            }

            const icon = fee?.properties?.Icon ?? parsed.Icon;

            if (icon) {
                details.icon = icon;
            }

            const fontName = fee?.properties?.Font ?? parsed.Font;

            if (fontName && type.properties?.fonts) {
                const font = type.properties.fonts.find((entry) => entry.name === fontName);

                if (font) {
                    details.font = font;
                }
            }

            const colorName = fee?.properties?.Color ?? parsed.Color?.split(' - ')[0]?.trim();

            if (colorName && type.properties?.colors) {
                const color = type.properties.colors.find((entry) => entry.name === colorName);

                if (color) {
                    details.color = color;
                }
            }

            return details;
        }

        const detailKey = type.label.toLowerCase();
        const optionLabel = parsed[type.label];
        const option = findImageOption(
            type,
            (entry) => entry.label === optionLabel || entry.value == fee?.variant_id
        );

        return option ? { [detailKey]: option } : {};
    }

    static hydrateStateFromCart(state, { baseItem, feeItems }) {
        const locationEntries = Object.entries(baseItem.properties ?? {})
            .filter(([key]) => /^Location_\d+$/.test(key))
            .sort(([a], [b]) => Number(a.split('_')[1]) - Number(b.split('_')[1]));

        const confirmedPersonalizations = [];
        const usedFeeKeys = new Set();

        for (const [locationKey, basePropertyValue] of locationEntries) {
            const parsed = PersonalizationCart.parseBasePropertyValue(basePropertyValue);
            const locationLabel = parsed.Location;
            const productLabel = parsed.Product ?? null;

            if (!locationLabel) {
                console.warn('Cart personalization edit: missing location in', locationKey);
                continue;
            }

            const match = PersonalizationCart.findProductAndPlacement(
                state.data,
                locationLabel,
                productLabel
            );

            if (!match) {
                console.warn('Cart personalization edit: placement not found', locationLabel, productLabel);
                continue;
            }

            const { product, placement } = match;
            const fee = PersonalizationCart.findFeeForLocation(
                feeItems,
                locationLabel,
                product?.label ?? productLabel,
                usedFeeKeys
            );
            const type =
                placement.types.find((entry) => entry.properties?.product?.id == fee?.variant_id)
                ?? placement.types.find((entry) => entry.label === parsed.Type);

            if (!type) {
                console.warn('Cart personalization edit: type not found', parsed.Type);
                continue;
            }

            if (fee?.key) {
                usedFeeKeys.add(fee.key);
            }
            const details = PersonalizationCart.buildDetailsFromCart(type, parsed, fee);
            const selections = { product, placement, type, details };
            const personalizationVariantId = fee?.variant_id;

            if (!personalizationVariantId) {
                console.warn('Cart personalization edit: missing fee variant', locationLabel);
                continue;
            }

            confirmedPersonalizations.push({
                locationKey,
                productLabel: product?.label ?? null,
                placementLabel: placement.label,
                selections,
                personalizationVariantId,
                price: fee?.price ?? 0,
                basePropertyValue,
                personalizationProperties: PersonalizationCart.formatPersonalizationProperties(
                    selections,
                    details,
                    getFileName(state.data.base_product)
                ),
            });
        }

        state.confirmedPersonalizations = confirmedPersonalizations;
        state.currentSelections = {
            product: state.getFirstProductWithUnconfiguredPlacements(),
            placement: null,
            type: null,
            details: null,
        };
        state.currentPersonalizationProduct = null;
        state.editingConfirmedIndex = null;
        state.steps.current = 1;
        state.renumberLocationKeys();
        PersonalizationCart.syncFromState(state, { includeCurrentDraft: false });
    }

    static clearSavedDesigns(variantId) {
        const draftKey = `calpak_personalization_${variantId}`;

        if (typeof window.storageAvailable === 'function' && window.storageAvailable()) {
            localStorage.removeItem(draftKey);

            const cartEdit = typeof window.expireStorageItem === 'function'
                ? window.expireStorageItem(PersonalizationCart.CART_EDIT_STORAGE_KEY)
                : null;

            if (cartEdit?.variantId === variantId) {
                localStorage.removeItem(PersonalizationCart.CART_EDIT_STORAGE_KEY);
            }
        }

        const modal = document.getElementById('personalization-modal');

        if (modal?.data?.base_product?.id === variantId && typeof modal.clearSavedDesigns === 'function') {
            modal.clearSavedDesigns();
        }
    }

    static startCartEdit({ baseItem, feeItems, groupKeys, quantities }) {
        const payload = { baseItem, feeItems, groupKeys, quantities, variantId: baseItem.variant_id };
        const modal = document.getElementById('personalization-modal');

        if (modal?.state && modal.data?.base_product?.id === baseItem.variant_id) {
            modal.state.resetAll();
            PersonalizationCart.hydrateStateFromCart(modal.state, { baseItem, feeItems });
            modal.state.cartEditGroupKeys = groupKeys;
            modal.state.cartEditQuantities = quantities ?? null;
            document.querySelector('cart-drawer')?.close?.();
            modal.setAttribute('open', 'true');
            return;
        }

        if (typeof window.setStorageItem === 'function' && baseItem.url) {
            window.setStorageItem(PersonalizationCart.CART_EDIT_STORAGE_KEY, payload, 1);
            window.location.href = baseItem.url;
        }
    }

    static async addToCart(state, actionEl) {
        PersonalizationCart.commitDraftIfComplete(state);
        PersonalizationCart.syncFromState(state, { includeCurrentDraft: false });

        const items = PersonalizationCart.getLineItems(state);

        if (!items.length) {
            return false;
        }

        actionEl.disabled = true;

        try {
            if (state.cartEditGroupKeys?.length) {
                const updates = Object.fromEntries(
                    state.cartEditGroupKeys.map((key) => [key, state.cartEditQuantities?.[key] ?? 0])
                );
                const removeResponse = await window.updateCart({ updates }, false);

                if (removeResponse?.status) {
                    console.error('Personalization cart edit replace failed', removeResponse);
                    return false;
                }

                state.cartEditGroupKeys = null;
                state.cartEditQuantities = null;
            }

            const response = await window.addToCart({ items });

            if (response?.status) {
                console.error('Personalization add to cart failed', response);
                return false;
            }

            return true;
        } finally {
            actionEl.disabled = false;
        }
    }

    #handleAddBound = null;
    #modal = null;

    connectedCallback() {
        this.#modal = this.closest('personalization-modal');
        this.#render();
        this.#handleAddBound = this.#handleAdd.bind(this);
        this.querySelector('[data-action="add"]')?.addEventListener('click', this.#handleAddBound);
    }

    disconnectedCallback() {
        this.querySelector('[data-action="add"]')?.removeEventListener('click', this.#handleAddBound);
        this.#modal = null;
    }

    #render() {
        const state = this.#modal?.state;

        if (!state) {
            return;
        }

        const canAdd = PersonalizationCart.canAddToCart(state);

        this.innerHTML = `
            <button
                type="button"
                class="personalization-modal--action cart btn--primary"
                data-action="add"
                data-cnstrc-btn="add_to_cart"
                ${canAdd ? '' : 'disabled'}
            >Accept Terms & Add to Bag</button>
        `;
    }

    async #handleAdd(ev) {
        const state = this.#modal?.state;
        const actionEl = ev.currentTarget;

        if (!state) {
            return;
        }

        const success = await PersonalizationCart.addToCart(state, actionEl);

        if (success) {
            this.#modal?.dispatchEvent(new CustomEvent('personalization-added', { bubbles: true }));
        }
    }
}

class PersonalizationTextInput extends HTMLElement {
    static isProfane(text) {
        if (!text?.trim()) {
            return false;
        }

        return profanityFilter.isProfane(text);
    }

    static sanitizeTextInput(value, properties) {
        const sanitized = value.replace(/[^A-Za-z1-9 ]/g, '');

        return properties?.uppercase ? sanitized.toUpperCase() : sanitized;
    }

    static getCharacterLimit(properties) {
        return properties.icons ? properties.character_limit + 1 : properties.character_limit;
    }

    static getTextCharacterLimit(properties, details) {
        const baseLimit = PersonalizationTextInput.getCharacterLimit(properties);

        if (details?.icon) {
            return Math.max(0, baseLimit - 1);
        }

        return baseLimit;
    }

    static enforceTextLength(text, limit) {
        if (!text) {
            return text;
        }

        return text.slice(0, limit);
    }

    static syncDetailsTextToLimit(state) {
        const type = state.currentSelections.type;
        const text = state.currentSelections.details?.text;

        if (!text || type?.type !== 'text' || !type?.properties) {
            return;
        }

        const normalized = PersonalizationTextInput.enforceTextLength(
            PersonalizationTextInput.sanitizeTextInput(text, type.properties),
            PersonalizationTextInput.getTextCharacterLimit(type.properties, state.currentSelections.details)
        );

        if (normalized === text) {
            return;
        }

        if (normalized) {
            state.currentSelections.details.text = normalized;
        } else {
            delete state.currentSelections.details.text;
        }
    }

    connectedCallback() {
        const { properties, details } = decodeAttr(this.getAttribute('context'));
        const textLimit = PersonalizationTextInput.getTextCharacterLimit(properties, details);
        const text = details?.text;
        const hasProfanity = PersonalizationTextInput.isProfane(text);
        const isUppercase = properties?.uppercase === true;

        this.innerHTML = `
            <div class="personalization-modal--input${hasProfanity ? ' personalization-modal--input--error' : ''}">
                <label for="personalization_details_text" class="personalization-modal--input-label">
                    Text — ${isUppercase ? 'Letters A–Z' : 'Letters a–z, A–Z'}, Numbers 1–9*
                </label>
                <div class="input--text">
                    <input
                        type="text"
                        id="personalization_details_text"
                        name="personalization_details_text"
                        maxlength="${textLimit}"
                        placeholder="${isUppercase ? 'ABC' : 'Abc'}"
                        pattern="${isUppercase ? '[A-Z1-9 ]+' : '[A-Za-z1-9 ]+'}"
                        title="${isUppercase ? 'Uppercase letters A–Z, numbers 1–9, and spaces only' : 'Letters A–Z, a–z, numbers 1–9, and spaces only'}"
                        ${text ? `value="${text}"` : ''}
                        data-step="details"
                        data-input="text"
                        ${hasProfanity ? 'aria-invalid="true" aria-describedby="personalization_details_text_error"' : ''}
                        required
                    >
                    <span class="input--text-characters">${text?.length || 0}/${textLimit}</span>
                </div>
                ${hasProfanity ? `
                    <p id="personalization_details_text_error" class="personalization-modal--input-error" role="alert">
                        Please revise your text
                    </p>
                ` : ''}
            </div>
        `;
    }

    updateFromDetails(properties, details) {
        const textLimit = PersonalizationTextInput.getTextCharacterLimit(properties, details);
        const text = details?.text ?? '';
        const hasProfanity = PersonalizationTextInput.isProfane(text);
        const elWrapper = this.querySelector('.personalization-modal--input');
        const elInput = this.querySelector('#personalization_details_text');
        const elCounter = this.querySelector('.input--text-characters');

        if (elCounter) {
            elCounter.textContent = `${text.length}/${textLimit}`;
        }

        if (elInput) {
            elInput.maxLength = textLimit;
        }

        elWrapper?.classList.toggle('personalization-modal--input--error', hasProfanity);

        if (elInput) {
            if (hasProfanity) {
                elInput.setAttribute('aria-invalid', 'true');
                elInput.setAttribute('aria-describedby', 'personalization_details_text_error');
            } else {
                elInput.removeAttribute('aria-invalid');
                elInput.removeAttribute('aria-describedby');
            }
        }

        let elError = this.querySelector('#personalization_details_text_error');

        if (hasProfanity) {
            if (!elError) {
                elError = document.createElement('p');
                elError.id = 'personalization_details_text_error';
                elError.className = 'personalization-modal--input-error';
                elError.setAttribute('role', 'alert');
                elError.textContent = 'Please revise your text';
                elWrapper?.appendChild(elError);
            }
        } else if (elError) {
            elError.remove();
        }
    }
}

class PersonalizationIconPicker extends HTMLElement {
    static isIconDisabled(details, properties) {
        const text = details?.text;

        return Boolean(
            text &&
            properties &&
            text.length === PersonalizationTextInput.getCharacterLimit(properties)
        );
    }

    static getIconFocusIndex(icons, details, properties) {
        const selectedIndex = icons.findIndex((icon) => details?.icon === icon.name);

        if (selectedIndex >= 0 && !PersonalizationIconPicker.isIconDisabled(details, properties)) {
            return selectedIndex;
        }

        return icons.findIndex(() => !PersonalizationIconPicker.isIconDisabled(details, properties));
    }

    static handleKeydown(ev) {
        const elInput = ev.target.closest('input[data-input="icon"]');

        if (!elInput) {
            return;
        }

        const elGroup = elInput.closest('[data-icon-options]');

        if (!elGroup) {
            return;
        }

        const elInputs = [...elGroup.querySelectorAll('input[data-input="icon"]:not(:disabled)')];

        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(ev.key)) {
            ev.preventDefault();

            const currentIndex = elInputs.indexOf(elInput);

            if (currentIndex === -1) {
                return;
            }

            let nextIndex = currentIndex;

            if (ev.key === 'Home') {
                nextIndex = 0;
            } else if (ev.key === 'End') {
                nextIndex = elInputs.length - 1;
            } else if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') {
                nextIndex = (currentIndex + 1) % elInputs.length;
            } else {
                nextIndex = (currentIndex - 1 + elInputs.length) % elInputs.length;
            }

            elInput.tabIndex = -1;
            elInputs[nextIndex].tabIndex = 0;
            elInputs[nextIndex].focus({ preventScroll: true });
            return;
        }

        if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();

            if (elInput.disabled) {
                return;
            }

            elInput.checked = !elInput.checked;
            elInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    connectedCallback() {
        const { icons, details, properties } = decodeAttr(this.getAttribute('context'));
        const focusIndex = PersonalizationIconPicker.getIconFocusIndex(icons, details, properties);
        const isDisabled = PersonalizationIconPicker.isIconDisabled(details, properties);

        this.innerHTML = `
            <fieldset class="personalization-modal--input"${isDisabled ? ' aria-describedby="personalization_details_icon_hint"' : ''}>
                <legend class="personalization-modal--input-label">
                    Icon
                </legend>
                ${isDisabled ? `
                    <p id="personalization_details_icon_hint" class="personalization-modal--input-hint small">
                        Icons are unavailable when your text reaches the character limit.
                    </p>
                ` : ''}
                <ul class="personalization-modal--options" data-icon-options>
                    ${icons.map((icon, index) => {
            const inputId = `personalization_details_icon_${icon.name}`;
            return `
                            <li class="personalization-modal--option input--icon">
                                <input
                                    id="${inputId}"
                                    type="checkbox"
                                    name="personalization_details_icon"
                                    value="${icon.name}"
                                    data-step="details"
                                    data-input="icon"
                                    tabindex="${index === focusIndex ? 0 : -1}"
                                    ${details?.icon == icon.name ? 'checked' : ''}
                                    ${isDisabled ? 'disabled' : ''}
                                >
                                <label for="${inputId}">
                                    <span class="visually-hidden">${icon.name}</span>
                                    <span role="presentation">${icon.svg}</span>
                                </label>
                            </li>
                        `;
        }).join('')}
                </ul>
            </fieldset>
        `;
    }

    updateDisabledState(details, properties) {
        const isDisabled = PersonalizationIconPicker.isIconDisabled(details, properties);
        const elFieldset = this.querySelector('fieldset.personalization-modal--input');
        let elHint = this.querySelector('#personalization_details_icon_hint');

        if (elFieldset) {
            if (isDisabled) {
                elFieldset.setAttribute('aria-describedby', 'personalization_details_icon_hint');
            } else {
                elFieldset.removeAttribute('aria-describedby');
            }
        }

        if (isDisabled && !elHint) {
            elHint = document.createElement('p');
            elHint.id = 'personalization_details_icon_hint';
            elHint.className = 'personalization-modal--input-hint small';
            elHint.textContent = 'Icons are unavailable when your text reaches the character limit.';
            elFieldset?.insertBefore(elHint, elFieldset.querySelector('[data-icon-options]'));
        } else if (!isDisabled && elHint) {
            elHint.remove();
        }

        for (const elInput of this.querySelectorAll('input[data-input="icon"]')) {
            elInput.disabled = isDisabled;
        }
    }
}

class PersonalizationFontPicker extends HTMLElement {
    #swiper = null;
    #swiperInitId = 0;
    #resizeObserver = null;

    connectedCallback() {
        const { fonts, details } = decodeAttr(this.getAttribute('context'));

        this.innerHTML = `
            <fieldset class="personalization-modal--input">
                <legend class="personalization-modal--input-label">
                    <span>Font*</span>
                </legend>
                <div class="personalization-modal--options-navigation">
                    <button
                        type="button"
                        class="personalization-modal--options-navigation-prev btn--arrow prev"
                        data-navigation="prev"
                        aria-label="Previous font"
                    ></button>
                    <button
                        type="button"
                        class="personalization-modal--options-navigation-next btn--arrow next"
                        data-navigation="next"
                        aria-label="Next font"
                    ></button>
                </div>
                <div class="personalization-modal--options-carousel swiper" data-options-carousel>
                    <ul class="swiper-wrapper">
                        ${fonts.map((font) => {
            const inputId = `personalization_details_font_${font.handle}`;
            const baselineAdjustment = formatPxToRem(font.baseline_adjustment);
            const fontSampleStyles = [
                'font-size: calc(1em * var(--scale))',
                'line-height: 1',
                baselineAdjustment ? `--baseline-adjustment: ${baselineAdjustment}` : '',
            ]
                .filter(Boolean)
                .join('; ');
            return `
                                <li class="personalization-modal--option input--radio swiper-slide" style="font-family: var(--font-${font.handle}); --scale: var(--size-${font.handle});">
                                    <input
                                        type="radio"
                                        id="${inputId}"
                                        name="personalization_details_font"
                                        value="${font.name}"
                                        data-step="details"
                                        data-input="font"
                                        ${details?.font?.name == font.name ? 'checked' : ''}
                                        required
                                    >
                                    <label for="${inputId}">
                                        <span class="visually-hidden">${font.name}</span>
                                        <span role="presentation" class="personalization-modal--font-sample" style="${fontSampleStyles}">
                                            AaBbCc
                                        </span>
                                    </label>
                                </li>
                            `;
        }).join('')}
                    </ul>
                </div>
            </fieldset>
        `;

        this.#initSwiper();
    }

    async #initSwiper() {
        const elCarousel = this.querySelector('[data-options-carousel]');

        if (!elCarousel) return;

        const initId = ++this.#swiperInitId;
        const Swiper = await loadSwiper({ waitForLoad: false });

        if (!this.isConnected || initId !== this.#swiperInitId) {
            return;
        }

        this.#swiper?.destroy(true, true);
        this.#swiper = null;
        this.#resizeObserver?.disconnect();

        this.#swiper = new Swiper(elCarousel, {
            slidesPerView: 4,
            spaceBetween: 12,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            navigation: {
                nextEl: this.querySelector('[data-navigation="next"]'),
                prevEl: this.querySelector('[data-navigation="prev"]'),
            },
            breakpoints: {
                1024: {
                    slidesPerView: 6,
                    spaceBetween: 12,
                },
            },
        });

        elCarousel.setAttribute('data-swiper-ready', '');

        this.#resizeObserver = new ResizeObserver(() => {
            if (initId !== this.#swiperInitId || !this.#swiper) {
                return;
            }

            this.#swiper.updateSize();
            this.#swiper.update();
        });
        this.#resizeObserver.observe(elCarousel);

        requestAnimationFrame(() => {
            if (initId !== this.#swiperInitId || !this.#swiper) {
                return;
            }

            this.#swiper.updateSize();
            this.#swiper.update();
        });
    }

    disconnectedCallback() {
        this.#swiperInitId += 1;
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
        this.#swiper?.destroy();
        this.#swiper = null;
    }
}

class PersonalizationColorPicker extends HTMLElement {
    connectedCallback() {
        const { colors, details } = decodeAttr(this.getAttribute('context'));

        this.innerHTML = `
            <fieldset class="personalization-modal--input">
                <legend class="personalization-modal--input-label">
                    Color*
                </legend>
                <ul class="personalization-modal--colors">
                    ${colors.map((color) => {
            const inputId = `personalization_details_color_${color.name}`;
            const isChecked = details?.color?.name == color.name;
            return `
                        <li>
                            <input
                                type="radio"
                                id="${inputId}"
                                name="personalization_details_color"
                                value="${color.name}"
                                data-step="details"
                                data-input="color"
                                class="visually-hidden"
                                ${isChecked ? 'checked' : ''}
                            >
                            <label for="${inputId}" class="personalization-modal--color">
                                <span role="presentation" class="input--swatch" style="--swatch: ${color.swatch};"></span>
                                <span class="visually-hidden">${color.name}</span>
                            </label>
                        </li>
                    `;
        }).join('')}
                </ul>
            </fieldset>
        `;
    }
}

class PersonalizationImagePicker extends HTMLElement {
    #categories = [];
    #key = '';
    #selectedValue = null;
    #selectedCategoryIndex = 0;
    #swiper = null;
    #swiperInitId = 0;

    static #getOptionsMarkup(options, key, selectedValue) {
        return options.map((option) => {
            const inputId = `personalization_details_${key}_${option.label.toLowerCase()}`;

            return `
                <li class="personalization-modal--option input--image transparent">
                    <input
                        id="${inputId}"
                        type="radio"
                        name="personalization_details_${key}"
                        value="${option.value}"
                        data-step="details"
                        data-input="${key}"
                        ${selectedValue == option.value ? 'checked' : ''}
                        required
                    />
                    <label for="${inputId}" class="transparent">
                        <span class="visually-hidden">${option.label}</span>
                        <span role="presentation" class="input--image-imag transparent">${option.image}</span>
                    </label>
                </li>
            `;
        }).join('');
    }

    #getCategoryIndexForValue(value) {
        if (value == null) {
            return 0;
        }

        const index = this.#categories.findIndex((category) =>
            category.options?.some((option) => option.value == value)
        );

        return index >= 0 ? index : 0;
    }

    #renderOptions() {
        const elOptions = this.querySelector('[data-image-options]');
        const category = this.#categories[this.#selectedCategoryIndex];

        if (!elOptions || !category) {
            return;
        }

        elOptions.dataset.categoryIndex = String(this.#selectedCategoryIndex);
        elOptions.innerHTML = PersonalizationImagePicker.#getOptionsMarkup(
            category.options ?? [],
            this.#key,
            this.#selectedValue
        );
        window.initLazyLoad?.();
    }

    #updateCategoryButtons() {
        this.querySelectorAll('button[data-category-index]').forEach((elButton) => {
            const index = Number(elButton.dataset.categoryIndex);
            const isSelected = index === this.#selectedCategoryIndex;

            elButton.classList.toggle('btn--primary', isSelected);
            elButton.classList.toggle('btn--secondary', !isSelected);
            elButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
    }

    #handleCategoryClick = (ev) => {
        const elButton = ev.target.closest('button[data-category-index]');

        if (!elButton || !this.contains(elButton)) {
            return;
        }

        const index = Number(elButton.dataset.categoryIndex);

        if (!Number.isFinite(index) || index === this.#selectedCategoryIndex) {
            return;
        }

        this.#selectedCategoryIndex = index;
        this.#updateCategoryButtons();
        this.#renderOptions();
        this.dispatchEvent(new CustomEvent('personalization-image-category-change', {
            bubbles: true,
            detail: { categoryIndex: index },
        }));
    };

    #handleChange = (ev) => {
        const elInput = ev.target.closest(`input[data-input="${this.#key}"]`);

        if (!elInput?.checked || !this.contains(elInput)) {
            return;
        }

        this.#selectedValue = elInput.value;
    };

    connectedCallback() {
        const { type, details, selectedCategoryIndex } = decodeAttr(this.getAttribute('context'));
        this.#categories = type.categories ?? [];
        this.#key = type.label.toLowerCase();
        this.#selectedValue = details?.[this.#key]?.value ?? null;

        if (
            selectedCategoryIndex != null &&
            Number.isFinite(Number(selectedCategoryIndex)) &&
            this.#categories[selectedCategoryIndex]
        ) {
            this.#selectedCategoryIndex = Number(selectedCategoryIndex);
        } else {
            this.#selectedCategoryIndex = this.#getCategoryIndexForValue(this.#selectedValue);
        }

        const selectedCategory = this.#categories[this.#selectedCategoryIndex];

        this.innerHTML = `
            <fieldset class="personalization-modal--input">
                <legend class="personalization-modal--input-label">
                    <span>${type.label}*</span>
                </legend>
                ${this.#categories.length ? `
                    <div class="personalization-modal--options-navigation">
                        <button
                            type="button"
                            class="personalization-modal--options-navigation-prev btn--arrow prev"
                            data-navigation="prev"
                            aria-label="Previous category"
                        ></button>
                        <button
                            type="button"
                            class="personalization-modal--options-navigation-next btn--arrow next"
                            data-navigation="next"
                            aria-label="Next category"
                        ></button>
                    </div>
                    <div
                        class="personalization-modal--image-categories personalization-modal--options-carousel swiper scrollbar--none"
                        data-category-carousel
                        role="group"
                        aria-label="${type.label} categories"
                    >
                        <ul class="swiper-wrapper">
                            ${this.#categories.map((category, index) => {
            const isSelected = index === this.#selectedCategoryIndex;

            return `
                                <li class="swiper-slide">
                                    <button
                                        type="button"
                                        class="${isSelected ? 'btn--primary' : 'btn--secondary'}"
                                        data-category-index="${index}"
                                        aria-pressed="${isSelected ? 'true' : 'false'}"
                                    >
                                        ${category.name}
                                    </button>
                                </li>
                            `;
        }).join('')}
                        </ul>
                    </div>
                ` : ''}
                <ul
                    class="personalization-modal--options personlization-modal--options-images"
                    data-image-options
                    data-category-index="${this.#selectedCategoryIndex}"
                >
                    ${PersonalizationImagePicker.#getOptionsMarkup(
            selectedCategory?.options ?? [],
            this.#key,
            this.#selectedValue
        )}
                </ul>
            </fieldset>
        `;

        this.addEventListener('click', this.#handleCategoryClick);
        this.addEventListener('change', this.#handleChange);

        window.initLazyLoad?.();
        this.#initCategorySwiper();
    }

    async #initCategorySwiper() {
        if (!this.querySelector('[data-category-carousel]')) {
            return;
        }

        const initId = ++this.#swiperInitId;
        const Swiper = await loadSwiper({ waitForLoad: false });

        if (!this.isConnected || initId !== this.#swiperInitId) {
            return;
        }

        const elCarousel = this.querySelector('[data-category-carousel]');

        if (!elCarousel) {
            return;
        }

        const slideCount = elCarousel.querySelectorAll('.swiper-slide').length;

        this.#swiper?.destroy(true, true);

        this.#swiper = new Swiper(elCarousel, {
            enabled: slideCount > 1,
            slidesPerView: 'auto',
            spaceBetween: 12,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            navigation: {
                nextEl: this.querySelector('[data-navigation="next"]'),
                prevEl: this.querySelector('[data-navigation="prev"]'),
            },
        });

        elCarousel.setAttribute('data-swiper-ready', '');

        requestAnimationFrame(() => {
            if (initId !== this.#swiperInitId || !this.#swiper) {
                return;
            }

            this.#swiper.updateSize();
            this.#swiper.update();
        });
    }

    disconnectedCallback() {
        this.#swiperInitId += 1;
        this.removeEventListener('click', this.#handleCategoryClick);
        this.removeEventListener('change', this.#handleChange);
        this.#swiper?.destroy();
        this.#swiper = null;
    }
}

class PersonalizationRadioGroup extends HTMLElement {
    connectedCallback() {
        const { step, options, selectedLabel, configuredLabels = [], required = true, legend, legendClass = 'personalization-modal--heading' } = decodeAttr(this.getAttribute('context'));

        const optionsHtml = `
            <ul class="personalization-modal--options">
                ${options.map((option, index) => {
            const inputName = `personalization_${step}`;
            const inputId = `personalization_${step}_${index}`;
            const value = optionValue(option);
            const isChecked = selectedLabel ? selectedLabel == value : false;
            const isConfigured = configuredLabels.includes(option.label);
            return `
                        <li class="personalization-modal--option input--radio${isConfigured ? ' personalization-modal--option--configured' : ''}">
                            <input
                                id="${inputId}"
                                type="radio"
                                name="${inputName}"
                                value="${value}"
                                data-step="${step}"
                                ${isChecked ? 'checked' : ''}
                                ${required ? 'required' : ''}
                            >
                            <label for="${inputId}">
                                ${option.label}
                                ${isConfigured ? '<span class="visually-hidden">, design added</span>' : ''}
                            </label>
                        </li>
                    `;
        }).join('')}
            </ul>
        `;

        this.innerHTML = legend
            ? `
                <fieldset class="personalization-modal--field">
                    <legend class="${legendClass}">${legend}</legend>
                    ${optionsHtml}
                </fieldset>
            `
            : optionsHtml;
    }
}

class PersonalizationProgress extends HTMLElement {
    connectedCallback() {
        const { current, total } = decodeAttr(this.getAttribute('context'));

        this.innerHTML = `
            <progress id="personalization_progress" class="personalization-modal--progress-bar" value="${current}" max="${total}"></progress>
            <label for="personalization_progress" class="personalization-modal--progress-label small">
                <span class="visually-hidden">You are viewing step ${current} of ${total}</span>
                <span role="presentation">${current}/${total} Steps</span>
            </label>
        `;
    }
}

class PersonalizationPreviewImage extends HTMLElement {
    static PRELOAD_LINK_DATA_ATTR = 'data-personalization-preload';

    static #PREVIEW_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    static #PREVIEW_NUMBERS = '123456789';

    static #iconViewBoxCache = new Map();

    static #formatCoordinate(value) {
        if (value == null || value === '') {
            return '50%';
        }

        const stringValue = String(value);

        return stringValue.includes('%') ? stringValue : `${stringValue}%`;
    }

    // Sizes are emitted as unitless px numbers so the stylesheet can scale them
    // against the preview's own width rather than the root font size.
    static #getPxValue(value) {
        if (value == null || value === '') {
            return null;
        }

        const numericValue = Number(String(value).trim().replace(/px$/i, ''));

        return Number.isFinite(numericValue) ? numericValue : null;
    }

    static #escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    static #parseImageMarkup(markup) {
        const parser = document.createElement('div');
        parser.innerHTML = markup;
        const img = parser.querySelector('img');

        if (!img) {
            return null;
        }

        return {
            src: img.getAttribute('src'),
            srcset: img.getAttribute('srcset'),
            sizes: img.getAttribute('sizes'),
        };
    }

    static preloadImage(src, srcset, sizes) {
        if (!src) {
            return;
        }

        const selector = `link[${PersonalizationPreviewImage.PRELOAD_LINK_DATA_ATTR}="${CSS.escape(src)}"]`;

        if (document.head.querySelector(selector)) {
            return;
        }

        const elLink = document.createElement('link');
        elLink.rel = 'preload';
        elLink.as = 'image';
        elLink.href = src;
        if (srcset) {
            elLink.setAttribute('imagesrcset', srcset);
        }
        if (sizes) {
            elLink.setAttribute('imagesizes', sizes);
        }
        elLink.setAttribute(PersonalizationPreviewImage.PRELOAD_LINK_DATA_ATTR, src);
        document.head.appendChild(elLink);
    }

    static preloadImages(personalizations) {
        const parser = document.createElement('div');

        for (const product of personalizations) {
            for (const placement of product.placements) {
                parser.innerHTML = placement.image;
                const img = parser.querySelector('img');

                if (!img) {
                    continue;
                }

                PersonalizationPreviewImage.preloadImage(
                    img.getAttribute('src'),
                    img.getAttribute('srcset'),
                    img.getAttribute('sizes')
                );
            }
        }
    }

    static getActivePlacement(currentSelections, confirmedPersonalizations = []) {
        if (currentSelections.placement) {
            return currentSelections.placement;
        }

        const productLabel = currentSelections.product?.label ?? null;
        const productConfirmed = confirmedPersonalizations.filter((entry) => entry.productLabel === productLabel);
        const lastConfirmed = productConfirmed[productConfirmed.length - 1];

        if (lastConfirmed?.selections?.placement) {
            return lastConfirmed.selections.placement;
        }

        return currentSelections.product?.placements?.[0] ?? null;
    }

    static getBaseImage(currentSelections, confirmedPersonalizations = []) {
        const placement = PersonalizationPreviewImage.getActivePlacement(
            currentSelections,
            confirmedPersonalizations
        );

        return placement?.image ?? '';
    }

    static #getSelectionOverlayMarkup(selections) {
        const { placement, type, details } = selections;

        if (!placement) {
            return '';
        }

        if (!type) {
            return PersonalizationPreviewImage.#getPlacementIndicatorMarkup(placement);
        }

        const positionStyles = PersonalizationPreviewImage.#getPositionStyles(placement, type);

        if (type.type === 'text') {
            const properties = type.properties ?? {};
            const icon = properties.icons?.find((entry) => entry.name === details?.icon);
            const hasText = Boolean(details?.text);
            const hasIcon = Boolean(icon);

            if (!hasText && !hasIcon) {
                return PersonalizationPreviewImage.#getTextOverlayMarkup(placement, type, positionStyles, {
                    text: PersonalizationPreviewImage.#getPreviewPlaceholderText(properties),
                    font: details?.font ?? properties.fonts?.[0],
                    color: details?.color ?? properties.colors?.[0],
                });
            }

            return PersonalizationPreviewImage.#getTextOverlayMarkup(placement, type, positionStyles, {
                text: hasText ? details.text : '',
                font: details?.font,
                color: details?.color,
                icon: hasIcon ? icon : null,
            });
        }

        if (type.type === 'image') {
            const detailKey = type.label.toLowerCase();
            const categoryIndex = selections.imageCategoryIndex;
            const categories = type.categories ?? [];
            const activeCategory = Number.isFinite(Number(categoryIndex))
                ? categories[Number(categoryIndex)]
                : categories[0];
            const selectedOption = details?.[detailKey] ?? activeCategory?.options?.[0];

            if (!selectedOption?.image) {
                return PersonalizationPreviewImage.#getPlacementIndicatorMarkup(placement, type);
            }

            return PersonalizationPreviewImage.#getImageOverlayMarkup(
                placement,
                type,
                positionStyles,
                selectedOption
            );
        }

        return '';
    }

    static getOverlayMarkup(
        currentSelections,
        confirmedPersonalizations = [],
        { editingConfirmedIndex = null } = {}
    ) {
        const activePlacement = PersonalizationPreviewImage.getActivePlacement(
            currentSelections,
            confirmedPersonalizations
        );

        if (!activePlacement) {
            return '';
        }

        const activeViewImage = activePlacement.image;
        const productLabel = currentSelections.product?.label ?? null;
        const overlays = [];

        for (const [index, entry] of confirmedPersonalizations.entries()) {
            if (entry.productLabel !== productLabel) {
                continue;
            }

            const { placement } = entry.selections ?? {};

            if (!placement || placement.image !== activeViewImage) {
                continue;
            }

            if (editingConfirmedIndex === index) {
                continue;
            }

            const overlay = PersonalizationPreviewImage.#getSelectionOverlayMarkup(entry.selections);

            if (overlay) {
                overlays.push(overlay);
            }
        }

        if (currentSelections.placement?.image === activeViewImage) {
            const draftOverlay = PersonalizationPreviewImage.#getSelectionOverlayMarkup(currentSelections);

            if (draftOverlay) {
                overlays.push(draftOverlay);
            }
        }

        return overlays.join('');
    }

    static #getScaleValue(value) {
        const scale = Number(value);

        return Number.isFinite(scale) && scale > 0 ? scale : 1;
    }

    static #getRotationValue(value) {
        const rotation = Number(value);

        return Number.isFinite(rotation) ? rotation : 0;
    }

    static #getPositionStyles(placement, type = null) {
        const coordinates = placement.coordinates ?? {};
        const fontSizes = placement.font_size ?? {};
        const placementRotation = PersonalizationPreviewImage.#getRotationValue(placement.rotation);
        const typeRotation = type ? PersonalizationPreviewImage.#getRotationValue(type.rotation) : 0;
        const mobileFontSize = PersonalizationPreviewImage.#getPxValue(fontSizes.mobile);
        const desktopFontSize = PersonalizationPreviewImage.#getPxValue(fontSizes.desktop);

        return [
            `--x-mobile: ${PersonalizationPreviewImage.#formatCoordinate(coordinates.mobile?.x)}`,
            `--y-mobile: ${PersonalizationPreviewImage.#formatCoordinate(coordinates.mobile?.y)}`,
            `--x-desktop: ${PersonalizationPreviewImage.#formatCoordinate(coordinates.desktop?.x)}`,
            `--y-desktop: ${PersonalizationPreviewImage.#formatCoordinate(coordinates.desktop?.y)}`,
            mobileFontSize === null ? '' : `--font-size-mobile-px: ${mobileFontSize}`,
            desktopFontSize === null ? '' : `--font-size-desktop-px: ${desktopFontSize}`,
            `--placement-rotation: ${placementRotation}deg`,
            `--type-rotation: ${typeRotation}deg`,
        ]
            .filter(Boolean)
            .join('; ');
    }

    static #getPlacementIndicatorMarkup(placement, type = null) {
        const positionStyles = PersonalizationPreviewImage.#getPositionStyles(placement, type);

        return `
            <div class="personalization-preview__overlay personalization-preview__overlay--indicator" style="${positionStyles}">
                <span class="personalization-preview__overlay-indicator" role="presentation"></span>
            </div>
        `;
    }

    static #takeFromPool(pool, count) {
        return pool.repeat(Math.ceil(count / pool.length)).slice(0, count);
    }

    static #getPreviewLetters(count, isUppercase) {
        const letters = PersonalizationPreviewImage.#takeFromPool(
            PersonalizationPreviewImage.#PREVIEW_LETTERS,
            count
        );

        return isUppercase ? letters : `${letters.slice(0, 1)}${letters.slice(1).toLowerCase()}`;
    }

    static #getPreviewPlaceholderText(properties) {
        const limit = Math.max(1, Math.trunc(Number(properties?.character_limit)) || 1);
        const isUppercase = properties?.uppercase === true;

        if (limit < 4) {
            return PersonalizationPreviewImage.#getPreviewLetters(limit, isUppercase);
        }

        // From four characters up numbers join the letters, and the two grow
        // alternately as the limit rises: ABC1, ABC12, ABCD12, ABCD123
        const letterCount = Math.ceil((limit + 1) / 2);
        const numberCount = limit - letterCount;
        const letters = PersonalizationPreviewImage.#getPreviewLetters(letterCount, isUppercase);
        const numbers = PersonalizationPreviewImage.#takeFromPool(
            PersonalizationPreviewImage.#PREVIEW_NUMBERS,
            numberCount
        );

        return `${letters}${numbers}`;
    }

    static #getTextOverlayMarkup(placement, type, positionStyles, { text, font, color, icon }) {
        const fontHandle = font?.handle;
        const threadColor = color?.thread;
        const typeScale = PersonalizationPreviewImage.#getScaleValue(type?.scale);
        const fontScale = PersonalizationPreviewImage.#getScaleValue(font?.scale);
        const overlayStyles = [
            positionStyles,
            `--type-scale: ${typeScale}`,
            threadColor ? `--color: #${threadColor}` : '',
        ]
            .filter(Boolean)
            .join('; ');
        const baselineAdjustment = PersonalizationPreviewImage.#getPxValue(font?.baseline_adjustment);
        const contentStyles = [
            `--font-scale: ${fontScale}`,
            fontHandle ? `--font-family: var(--font-${fontHandle})` : '',
            baselineAdjustment ? `--baseline-adjustment-px: ${baselineAdjustment}` : '',
        ]
            .filter(Boolean)
            .join('; ');

        return `
            <div class="personalization-preview__overlay personalization-preview__overlay--text" style="${overlayStyles}">
                <span class="personalization-preview__overlay-content"${contentStyles ? ` style="${contentStyles}"` : ''}>
                    ${text ? `<span class="personalization-preview__overlay-text">${PersonalizationPreviewImage.#escapeHtml(text)}</span>` : ''}
                    ${icon ? `<span class="personalization-preview__overlay-icon" role="presentation">${icon.svg}</span>` : ''}
                </span>
            </div>
        `;
    }

    static #getImageOverlayMarkup(placement, type, positionStyles, option) {
        const imageMarkup = PersonalizationPreviewImage.#parseImageMarkup(option.image);
        const typeScale = PersonalizationPreviewImage.#getScaleValue(type?.scale);
        const imageStyles = [positionStyles, `--type-scale: ${typeScale}`].join('; ');

        if (imageMarkup) {
            PersonalizationPreviewImage.preloadImage(
                imageMarkup.src,
                imageMarkup.srcset,
                imageMarkup.sizes
            );
        }

        return `
            <div class="personalization-preview__overlay personalization-preview__overlay--image transparent" style="${imageStyles}">
                <span role="presentation" class="personalization-preview__overlay-image transparent">${option.image}</span>
            </div>
        `;
    }

    static #getStrokeInset(elSvg) {
        const shapes = elSvg.querySelectorAll('path, circle, rect, polygon, polyline, line, ellipse');
        let widest = 0;

        for (const shape of shapes) {
            const { stroke, strokeWidth } = getComputedStyle(shape);

            if (!stroke || stroke === 'none') {
                continue;
            }

            widest = Math.max(widest, parseFloat(strokeWidth) || 0);
        }

        // A stroke straddles the path, so only half of it sits outside the geometry bounds.
        return widest / 2;
    }

    static #applyIconViewBox(elSvg, viewBox) {
        // The intrinsic ratio has to come from the viewBox, otherwise the width/height
        // attributes keep the old framing and preserveAspectRatio letterboxes the artwork.
        elSvg.removeAttribute('width');
        elSvg.removeAttribute('height');
        elSvg.setAttribute('viewBox', viewBox);
    }

    static #normaliseIconViewBoxes(elRoot) {
        for (const elSvg of elRoot.querySelectorAll('.personalization-preview__overlay-icon svg')) {
            const cacheKey = elSvg.outerHTML;
            const cached = PersonalizationPreviewImage.#iconViewBoxCache.get(cacheKey);

            if (cached) {
                PersonalizationPreviewImage.#applyIconViewBox(elSvg, cached);
                continue;
            }

            let bounds = null;

            try {
                bounds = elSvg.getBBox();
            } catch {
                continue;
            }

            // Bail without caching when the icon has no layout yet, so a later render retries.
            if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
                continue;
            }

            const inset = PersonalizationPreviewImage.#getStrokeInset(elSvg);
            const viewBox = [
                bounds.x - inset,
                bounds.y - inset,
                bounds.width + inset * 2,
                bounds.height + inset * 2,
            ]
                .map((value) => Number(value.toFixed(3)))
                .join(' ');

            PersonalizationPreviewImage.#iconViewBoxCache.set(cacheKey, viewBox);
            PersonalizationPreviewImage.#applyIconViewBox(elSvg, viewBox);
        }
    }

    static #ensurePreviewImagesVisible(elRoot) {
        for (const elImg of elRoot.querySelectorAll('.personalization-preview img')) {
            elImg.loading = 'eager';
            elImg.classList.add('in-view');
        }

        window.initLazyLoad?.();
    }

    connectedCallback() {
        const selections = decodeAttr(this.getAttribute('selections'));
        const confirmedPersonalizations = decodeAttr(this.getAttribute('confirmed-personalizations')) ?? [];
        const editingConfirmedIndexAttr = this.getAttribute('editing-confirmed-index');
        const editingConfirmedIndex =
            editingConfirmedIndexAttr === null || editingConfirmedIndexAttr === ''
                ? null
                : Number(editingConfirmedIndexAttr);
        const personalizationsAttr = this.getAttribute('personalizations');

        if (personalizationsAttr) {
            PersonalizationPreviewImage.preloadImages(decodeAttr(personalizationsAttr));
        }

        const baseImage = PersonalizationPreviewImage.getBaseImage(selections, confirmedPersonalizations);
        const overlayMarkup = PersonalizationPreviewImage.getOverlayMarkup(selections, confirmedPersonalizations, {
            editingConfirmedIndex,
        });

        this.innerHTML = `
            <div class="personalization-preview" aria-hidden="true">
                <div class="personalization-preview__base transparent">${baseImage}</div>
                ${overlayMarkup}
            </div>
        `;

        PersonalizationPreviewImage.#ensurePreviewImagesVisible(this);
        PersonalizationPreviewImage.#normaliseIconViewBoxes(this);

        requestAnimationFrame(() => {
            if (!this.isConnected) {
                return;
            }

            PersonalizationPreviewImage.#normaliseIconViewBoxes(this);
        });

        PersonalizationPreviewImage.#markDraftOverlay(this, selections);
    }

    static #markDraftOverlay(element, selections) {
        const elPreview = element.querySelector('.personalization-preview');

        if (!elPreview || !selections.placement) {
            return;
        }

        const overlays = [...elPreview.querySelectorAll('.personalization-preview__overlay')];
        const elDraft = overlays.at(-1);

        if (!elDraft) {
            return;
        }

        elDraft.setAttribute('data-personalization-draft', '');

        const details = selections.details ?? {};
        const properties = selections.type?.properties ?? {};
        const hasText = Boolean(details.text);
        const hasIcon = Boolean(properties.icons?.find((entry) => entry.name === details.icon));
        elDraft.dataset.placeholder = !hasText && !hasIcon ? 'true' : 'false';
    }

    static patchDraftOverlay(element, selections) {
        const elPreview = element.querySelector('.personalization-preview');

        if (!elPreview || selections.type?.type !== 'text') {
            return;
        }

        const details = selections.details ?? {};
        const properties = selections.type?.properties ?? {};
        const hasText = Boolean(details.text);
        const hasIcon = Boolean(properties.icons?.find((entry) => entry.name === details.icon));
        const usesPlaceholder = !hasText && !hasIcon;
        let elDraft = elPreview.querySelector('[data-personalization-draft]');

        if (!elDraft) {
            const overlays = [...elPreview.querySelectorAll('.personalization-preview__overlay')];
            elDraft = overlays.at(-1) ?? null;

            if (elDraft) {
                elDraft.setAttribute('data-personalization-draft', '');
            }
        }

        if (elDraft && !usesPlaceholder && hasText) {
            const elText = elDraft.querySelector('.personalization-preview__overlay-text');
            const elIcon = elDraft.querySelector('.personalization-preview__overlay-icon');

            if (
                elText &&
                elDraft.dataset.placeholder !== 'true' &&
                Boolean(elIcon) === hasIcon
            ) {
                elText.textContent = details.text;
                return;
            }
        }

        const draftMarkup = PersonalizationPreviewImage.#getSelectionOverlayMarkup(selections);

        if (elDraft) {
            elDraft.remove();
        }

        if (!draftMarkup) {
            return;
        }

        const parser = document.createElement('div');
        parser.innerHTML = draftMarkup.trim();
        const elNewDraft = parser.firstElementChild;

        if (!elNewDraft) {
            return;
        }

        elNewDraft.setAttribute('data-personalization-draft', '');
        elNewDraft.dataset.placeholder = usesPlaceholder ? 'true' : 'false';
        elPreview.appendChild(elNewDraft);
        PersonalizationPreviewImage.#normaliseIconViewBoxes(element);
    }
}

class PersonalizationStepActions extends HTMLElement {
    connectedCallback() {
        const { current, total, canNext, showSave } = decodeAttr(this.getAttribute('context'));

        this.innerHTML = `
            ${current > 1 ? `<button type="button" class="personalization-modal--action prev btn--secondary" data-action="prev">Back</button>` : ''}
            ${current < total ? `<button type="button" class="personalization-modal--action next btn--primary" data-action="next" ${canNext ? '' : 'disabled'}>Next</button>` : ''}
            ${showSave ? `<button type="button" class="personalization-modal--action save btn--tertiary border" data-action="save">Add More Designs</button>` : ''}
        `;
    }

    updateCanNext(canNext) {
        const elNext = this.querySelector('[data-action="next"]');

        if (elNext) {
            elNext.disabled = !canNext;
        }
    }
}

class PersonalizationStepPlacement extends HTMLElement {
    static findConfirmedEntryIndex(state, placement, product = state.currentSelections.product) {
        return state.confirmedPersonalizations.findIndex(
            (entry) =>
                entry.placementLabel === placement.label &&
                entry.productLabel === (product?.label ?? null)
        );
    }

    static isPlacementConfigured(state, placement, product = state.currentSelections.product) {
        return PersonalizationStepPlacement.findConfirmedEntryIndex(state, placement, product) !== -1;
    }

    static beginPlacementEdit(state, placement, entryIndex) {
        const entry = state.confirmedPersonalizations[entryIndex];
        const { product, type, details } = entry.selections;

        state.editingConfirmedIndex = entryIndex;
        state.currentSelections.product = product ?? state.currentSelections.product;
        state.currentSelections.placement = placement;
        state.currentSelections.type = type ?? null;
        state.currentSelections.details = details ? { ...details } : null;
        state.currentPersonalizationProduct = {
            id: entry.personalizationVariantId,
            price: entry.price,
        };
    }

    static isCurrentPlacementConfirmed(state) {
        const { placement, product } = state.currentSelections;

        if (!placement) {
            return false;
        }

        if (state.editingConfirmedIndex !== null) {
            const editingEntry = state.confirmedPersonalizations[state.editingConfirmedIndex];

            if (
                editingEntry?.placementLabel === placement.label &&
                editingEntry?.productLabel === (product?.label ?? null)
            ) {
                return false;
            }
        }

        return PersonalizationStepPlacement.isPlacementConfigured(state, placement, product);
    }

    static isComplete(state) {
        return Boolean(state.currentSelections.placement);
    }

    static handleSelection(state, data, target) {
        const { value, dataset } = target;
        const { step } = dataset;
        let valueObject = {};
        let clearDownstream = null;
        let currentPersonalizationProduct;

        if (step === 'product') {
            valueObject = data.personalizations.find((product) => product.label == value);

            if (valueObject?.label !== state.currentSelections.product?.label) {
                clearDownstream = ['placement', 'type', 'details'];
                currentPersonalizationProduct = null;
                state.editingConfirmedIndex = null;
            }
        } else if (step === 'placement') {
            valueObject = state.currentSelections.product.placements.find((placement) => placement.label == value);
            const entryIndex = PersonalizationStepPlacement.findConfirmedEntryIndex(
                state,
                valueObject,
                state.currentSelections.product
            );

            if (entryIndex !== -1) {
                PersonalizationStepPlacement.beginPlacementEdit(state, valueObject, entryIndex);
                currentPersonalizationProduct = state.currentPersonalizationProduct;
            } else {
                state.editingConfirmedIndex = null;
                clearDownstream = ['type', 'details'];
                currentPersonalizationProduct = null;
            }
        }

        return {
            step,
            valueObject,
            clearDownstream,
            currentPersonalizationProduct,
            syncCart: { includeCurrentDraft: false },
        };
    }

    static beforeRender() { }

    static getOptionGroupFingerprints(state, data) {
        const fingerprints = [];

        if (data.personalizations.length > 1) {
            fingerprints.push(optionGroupFingerprint('product', data.personalizations));
        }

        if (state.currentSelections.product?.placements?.length) {
            fingerprints.push(optionGroupFingerprint('placement', state.currentSelections.product.placements));
        }

        return fingerprints.filter(Boolean);
    }

    connectedCallback() {
        const { data, currentSelections, confirmedPersonalizations } = decodeAttr(this.getAttribute('context'));
        const hasMultipleProducts = data.personalizations.length > 1;
        const state = { confirmedPersonalizations, currentSelections };

        const configuredLabels = currentSelections.product.placements
            .filter((placement) => PersonalizationStepPlacement.isPlacementConfigured(state, placement, currentSelections.product))
            .map((placement) => placement.label);
        const hasMultiplePlacements = currentSelections.product.placements.length > 1;
        const placementHeading = hasMultiplePlacements && configuredLabels.length > 0
            ? 'Select Another Placement'
            : 'Select Your Placement';

        this.innerHTML = `
            ${hasMultipleProducts ? `
                <personalization-radio-group context="${encodeAttr({
            step: 'product',
            options: data.personalizations,
            selectedLabel: currentSelections.product?.label,
            legend: 'Select Your Product',
        })}"></personalization-radio-group>
            ` : ''}
            <personalization-radio-group context="${encodeAttr({
            step: 'placement',
            options: currentSelections.product.placements,
            selectedLabel: currentSelections.placement?.label,
            configuredLabels,
            legend: placementHeading,
        })}"></personalization-radio-group>
        `;
    }
}

class PersonalizationStepType extends HTMLElement {
    static isComplete(state) {
        return Boolean(state.currentSelections.type);
    }

    static handleSelection(state, data, target) {
        const { value } = target;
        const valueObject =
            state.currentSelections.placement.types.find((type) => optionValue(type) == value)
            ?? state.currentSelections.placement.types.find((type) => type.label == value);

        if (!valueObject) {
            return;
        }

        let clearDownstream = null;
        let currentPersonalizationProduct = valueObject.type == 'text' ? valueObject.properties.product : null;
        const previousType = state.currentSelections.type;

        if (
            valueObject.type !== previousType?.type
            || (valueObject.label === previousType?.label && optionValue(valueObject) !== optionValue(previousType))
        ) {
            clearDownstream = ['details'];
        }

        return {
            step: 'type',
            valueObject,
            clearDownstream,
            currentPersonalizationProduct,
            syncCart: { includeCurrentDraft: false },
        };
    }

    static beforeRender() { }

    static getOptionGroupFingerprints(state) {
        const types = state.currentSelections.placement?.types;

        if (!types?.length) {
            return [];
        }

        const fingerprint = optionGroupFingerprint('type', types);
        return fingerprint ? [fingerprint] : [];
    }

    connectedCallback() {
        const { currentSelections } = decodeAttr(this.getAttribute('context'));
        const textTypes = currentSelections.placement
            ? currentSelections.placement.types.filter((type) => type.type == 'text')
            : [];

        this.innerHTML = `
            ${currentSelections.placement ? `
                <personalization-radio-group context="${encodeAttr({
            step: 'type',
            options: currentSelections.placement.types,
            selectedLabel: optionValue(currentSelections.type),
            legend: 'Choose Personalization Type',
        })}"></personalization-radio-group>
            ` : ''}
            ${textTypes.length ? `
                <div class="personalization-modal--note small">
                    ${textTypes.map((type) => `${type.label} – Max ${PersonalizationTextInput.getCharacterLimit(type.properties)} characters.`).join('<br />')}
                </div>
            ` : ''}
        `;
    }
}

class PersonalizationStepDetails extends HTMLElement {
    static isDraftComplete(state) {
        const { placement, type, details } = state.currentSelections;

        if (!placement || !type) {
            return false;
        }

        switch (type.type) {
            case 'text':
                if (details?.text && PersonalizationTextInput.isProfane(details.text)) {
                    return false;
                }

                return Boolean(details?.text) || Boolean(details?.icon);
            case 'image':
                return Boolean(details?.[type.label.toLowerCase()]);
            default:
                return false;
        }
    }

    static isComplete(state) {
        return PersonalizationStepDetails.isDraftComplete(state);
    }

    static handleSelection(state, data, target) {
        const { value, dataset } = target;
        const { step, input: selection } = dataset;

        if (step === 'type') {
            return PersonalizationStepType.handleSelection(state, data, target);
        }

        let valueObject = { ...state.currentSelections.details };
        let sanitizedSelectionStart = null;
        let sanitizedSelectionEnd = null;
        let currentPersonalizationProduct;

        let imageCategoryIndex;

        if (selection === 'text') {
            const limit = state.currentSelections.type?.properties
                ? PersonalizationTextInput.getTextCharacterLimit(state.currentSelections.type.properties, valueObject)
                : null;

            const properties = state.currentSelections.type?.properties;
            let detailValue = PersonalizationTextInput.sanitizeTextInput(value, properties);

            if (limit !== null) {
                detailValue = PersonalizationTextInput.enforceTextLength(detailValue, limit);
            }

            if (detailValue !== value && typeof target.selectionStart === 'number') {
                let beforeCursor = PersonalizationTextInput.sanitizeTextInput(value.slice(0, target.selectionStart), properties);

                if (limit !== null) {
                    beforeCursor = PersonalizationTextInput.enforceTextLength(beforeCursor, limit);
                }

                sanitizedSelectionStart = beforeCursor.length;
                sanitizedSelectionEnd = beforeCursor.length;
            }

            if (detailValue) {
                valueObject.text = detailValue;
            } else {
                delete valueObject.text;
            }
        } else if (selection === 'icon') {
            if (target.checked) {
                valueObject.icon = value;

                if (valueObject.text && state.currentSelections.type?.properties) {
                    const limit = PersonalizationTextInput.getTextCharacterLimit(
                        state.currentSelections.type.properties,
                        valueObject
                    );
                    valueObject.text = PersonalizationTextInput.enforceTextLength(
                        PersonalizationTextInput.sanitizeTextInput(valueObject.text, state.currentSelections.type.properties),
                        limit
                    );
                }
            } else {
                delete valueObject.icon;
            }
        } else if (selection === state.currentSelections.type?.label.toLowerCase()) {
            imageCategoryIndex = Number(target.closest('[data-image-options]')?.dataset.categoryIndex);
            const resolvedCategoryIndex = Number.isFinite(imageCategoryIndex)
                ? imageCategoryIndex
                : state.currentSelections.imageCategoryIndex;

            valueObject[selection] = findImageOption(
                state.currentSelections.type,
                (option) => option.value == value,
                resolvedCategoryIndex
            );

            if (!valueObject[selection]) {
                delete valueObject[selection];
            }
        } else {
            const propertyKey = selection === 'color' ? 'colors' : selection === 'font' ? 'fonts' : selection;
            valueObject[selection] = state.currentSelections.type?.properties?.[propertyKey]?.find(
                (option) => option.name == value
            );
        }

        if (state.currentSelections.type?.type == 'image') {
            currentPersonalizationProduct = valueObject[selection]?.product;
        }

        return {
            step: 'details',
            valueObject,
            currentPersonalizationProduct,
            imageCategoryIndex: Number.isFinite(imageCategoryIndex) ? imageCategoryIndex : undefined,
            sanitizedSelectionStart,
            sanitizedSelectionEnd,
            syncCart: { includeCurrentDraft: true },
        };
    }

    static beforeRender(state) {
        if (state.steps.current !== 3) {
            return;
        }

        const properties = state.currentSelections.type?.properties;

        if (state.currentSelections.type?.type !== 'text' || !properties) {
            return;
        }

        const fonts = properties.fonts;
        const colors = properties.colors;

        if (!fonts?.length && !colors?.length) {
            return;
        }

        if (!state.currentSelections.details) {
            state.currentSelections.details = {};
        }

        const details = state.currentSelections.details;

        if (fonts?.length && !details.font) {
            details.font = fonts[0];
        }

        if (colors?.length && !details.color) {
            details.color = colors[0];
        }

        PersonalizationTextInput.syncDetailsTextToLimit(state);
    }

    static handleKeydown(ev) {
        PersonalizationIconPicker.handleKeydown(ev);
    }

    static getOptionGroupFingerprints(state) {
        return PersonalizationStepType.getOptionGroupFingerprints(state);
    }

    connectedCallback() {
        const { currentSelections } = decodeAttr(this.getAttribute('context'));
        const type = currentSelections.type;
        const properties = type?.properties;
        const details = currentSelections.details ?? {};

        let detailPickers = '';

        if (type?.type == 'text' && properties) {
            detailPickers = `
                <personalization-text-input context="${encodeAttr({ properties, details })}"></personalization-text-input>
                ${properties.icons ? `<personalization-icon-picker context="${encodeAttr({ icons: properties.icons, details, properties })}"></personalization-icon-picker>` : ''}
                ${properties.fonts ? `<personalization-font-picker context="${encodeAttr({ fonts: properties.fonts, details })}"></personalization-font-picker>` : ''}
                ${properties.colors ? `<personalization-color-picker context="${encodeAttr({ colors: properties.colors, details })}"></personalization-color-picker>` : ''}
            `;
        } else if (type?.type == 'image') {
            detailPickers = `<personalization-image-picker context="${encodeAttr({
                type,
                details,
                selectedCategoryIndex: currentSelections.imageCategoryIndex ?? null,
            })}"></personalization-image-picker>`;
        }

        this.innerHTML = `
            <div class="personalization-modal--field">
                <p class="personalization-modal--heading">Add Details</p>
                <span class="personalization-modal--note small">* Required</span>
                ${currentSelections.placement ? `
                    <personalization-radio-group context="${encodeAttr({
            step: 'type',
            options: currentSelections.placement.types,
            selectedLabel: optionValue(currentSelections.type),
            legend: 'Choose Personalization Type',
            legendClass: 'visually-hidden',
        })}"></personalization-radio-group>
                ` : ''}
            </div>
            ${detailPickers}
        `;
    }
}

class PersonalizationStepConfirmation extends HTMLElement {
    static isComplete() {
        return true;
    }

    static handleSelection() {
        return null;
    }

    static beforeRender() { }

    static getOptionGroupFingerprints() {
        return [];
    }

    static commitUnsavedDraft(state, { persistDraft } = {}) {
        if (
            !PersonalizationStepDetails.isDraftComplete(state) ||
            PersonalizationStepPlacement.isCurrentPlacementConfirmed(state)
        ) {
            return false;
        }

        state.commitCurrentDraft();
        state.currentSelections = {
            product: state.currentSelections.product,
            placement: null,
            type: null,
            details: null,
        };
        state.currentPersonalizationProduct = null;
        persistDraft?.();

        return true;
    }

    static beginEntryEdit(state, entryIndex, { persistDraft } = {}) {
        PersonalizationStepConfirmation.commitUnsavedDraft(state, { persistDraft });

        const entry = state.confirmedPersonalizations[entryIndex];
        const placement = entry?.selections?.placement;

        if (!placement) {
            return null;
        }

        PersonalizationStepPlacement.beginPlacementEdit(state, placement, entryIndex);
        state.steps.current = 1;

        return { syncCart: { includeCurrentDraft: true } };
    }

    static beginDraftEdit(state) {
        state.steps.current = 1;

        return { syncCart: { includeCurrentDraft: true } };
    }

    static handleAction(state, actionEl, { persistDraft } = {}) {
        const { confirmationTarget, entryIndex: entryIndexAttr } = actionEl.dataset;

        if (confirmationTarget === 'draft') {
            return PersonalizationStepConfirmation.beginDraftEdit(state);
        }

        const entryIndex = Number(entryIndexAttr);

        if (!Number.isNaN(entryIndex)) {
            return PersonalizationStepConfirmation.beginEntryEdit(state, entryIndex, { persistDraft });
        }

        return null;
    }

    static #clearInProgressSelections(state) {
        state.currentSelections = {
            product: state.currentSelections.product,
            placement: null,
            type: null,
            details: null,
        };
        state.currentPersonalizationProduct = null;
        state.editingConfirmedIndex = null;
    }

    static #hasUncommittedConfirmationDraft(state) {
        return (
            PersonalizationStepDetails.isDraftComplete(state) &&
            !PersonalizationStepPlacement.isCurrentPlacementConfirmed(state)
        );
    }

    static #setStepAfterRemoval(state) {
        const hasRemainingPersonalizations =
            state.confirmedPersonalizations.length > 0 ||
            PersonalizationStepConfirmation.#hasUncommittedConfirmationDraft(state);

        state.steps.current = hasRemainingPersonalizations ? state.steps.total : 1;
    }

    static #clearDraftForRemovedEntry(state, removedEntry) {
        const { placement, product } = state.currentSelections;

        if (
            !placement ||
            placement.label !== removedEntry.placementLabel ||
            (product?.label ?? null) !== removedEntry.productLabel
        ) {
            return;
        }

        PersonalizationStepConfirmation.#clearInProgressSelections(state);
    }

    static removeConfirmedEntry(state, entryIndex, { persistDraft } = {}) {
        if (entryIndex < 0 || entryIndex >= state.confirmedPersonalizations.length) {
            return null;
        }

        const removedEntry = state.confirmedPersonalizations[entryIndex];
        state.confirmedPersonalizations.splice(entryIndex, 1);
        state.renumberLocationKeys();

        if (state.editingConfirmedIndex === entryIndex) {
            PersonalizationStepConfirmation.#clearInProgressSelections(state);
        } else if (state.editingConfirmedIndex > entryIndex) {
            state.editingConfirmedIndex -= 1;
        } else {
            PersonalizationStepConfirmation.#clearDraftForRemovedEntry(state, removedEntry);
        }

        PersonalizationStepConfirmation.#setStepAfterRemoval(state);
        persistDraft?.();

        return {
            syncCart: {
                includeCurrentDraft: PersonalizationStepConfirmation.#hasUncommittedConfirmationDraft(state),
            },
        };
    }

    static removeDraft(state, { persistDraft } = {}) {
        if (state.editingConfirmedIndex !== null) {
            return PersonalizationStepConfirmation.removeConfirmedEntry(state, state.editingConfirmedIndex, {
                persistDraft,
            });
        }

        PersonalizationStepConfirmation.#clearInProgressSelections(state);
        PersonalizationStepConfirmation.#setStepAfterRemoval(state);
        persistDraft?.();

        return { syncCart: { includeCurrentDraft: false } };
    }

    static handleRemove(state, actionEl, { persistDraft } = {}) {
        const { confirmationTarget, entryIndex: entryIndexAttr } = actionEl.dataset;

        if (confirmationTarget === 'draft') {
            return PersonalizationStepConfirmation.removeDraft(state, { persistDraft });
        }

        const entryIndex = Number(entryIndexAttr);

        if (!Number.isNaN(entryIndex)) {
            return PersonalizationStepConfirmation.removeConfirmedEntry(state, entryIndex, { persistDraft });
        }

        return null;
    }

    static showAddMoreDesigns(state) {
        if (
            state.steps.current !== state.steps.total ||
            !state.hasAnyUnconfiguredPlacements()
        ) {
            return false;
        }

        if (!PersonalizationStepDetails.isDraftComplete(state)) {
            return !state.currentSelections.placement;
        }

        return !PersonalizationStepPlacement.isCurrentPlacementConfirmed(state);
    }

    static save(state, { persistDraft, render }) {
        if (state.steps.current !== state.steps.total) {
            return;
        }

        const draftComplete = PersonalizationStepDetails.isDraftComplete(state);

        if (
            draftComplete &&
            PersonalizationStepPlacement.isCurrentPlacementConfirmed(state)
        ) {
            return;
        }

        if (draftComplete) {
            state.commitCurrentDraft();
            PersonalizationCart.syncFromState(state, { includeCurrentDraft: false });
        }

        state.currentSelections = {
            product: state.getFirstProductWithUnconfiguredPlacements(),
            placement: null,
            type: null,
            details: null,
        };

        state.currentPersonalizationProduct = null;
        state.editingConfirmedIndex = null;
        state.steps.current = state.hasAnyUnconfiguredPlacements() ? 1 : state.steps.total;
        persistDraft();
        render();
    }

    connectedCallback() {
        const {
            data,
            currentSelections,
            confirmedPersonalizations,
            editingConfirmedIndex,
            steps,
            currentPersonalizationProduct,
            confirmationHeading,
            confirmationDescription,
            termsHtml,
        } = decodeAttr(this.getAttribute('context'));

        const state = {
            data,
            currentSelections,
            confirmedPersonalizations,
            editingConfirmedIndex: editingConfirmedIndex ?? null,
            steps,
            currentPersonalizationProduct,
        };

        const { rows, subtotal } = PersonalizationCart.getConfirmationPricing(state);

        this.innerHTML = `
            <div class="personalization-modal--field">
                <h3 class="personalization-modal--heading">${confirmationHeading}</h3>
                <div class="personalization-modal--confirmation-description">
                    ${confirmationDescription}
                </div>
            </div>
            <table class="personalization-modal--confirmation-items">
                <thead class="visually-hidden">
                    <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="personalization-modal--confirmation-product">${data.base_product.title}</td>
                        <td class="personalization-modal--confirmation-price">${window.formatMoney(data.base_product.price)}</td>
                        <td class="personalization-modal--confirmation-actions"></td>
                    </tr>
                    ${rows.map((row) => {
            const editAttrs = row.isDraft && row.entryIndex === null
                ? 'data-action="edit" data-confirmation-target="draft"'
                : `data-action="edit" data-entry-index="${row.entryIndex}"`;
            const removeAttrs = row.isDraft && row.entryIndex === null
                ? 'data-action="remove" data-confirmation-target="draft"'
                : `data-action="remove" data-entry-index="${row.entryIndex}"`;

            return `
                        <tr>
                            <td class="personalization-modal--confirmation-product">${row.summary}</td>
                            <td class="personalization-modal--confirmation-price">${window.formatMoney(row.price)}</td>
                            <td class="personalization-modal--confirmation-actions">
                                <button
                                    type="button"
                                    class="personalization-modal--confirmation-action btn--edit"
                                    ${editAttrs}
                                    aria-label="Edit ${row.summary}"
                                ></button>
                                <button
                                    type="button"
                                    class="personalization-modal--confirmation-action btn--remove"
                                    ${removeAttrs}
                                    aria-label="Remove ${row.summary}"
                                ></button>
                            </td>
                        </tr>
                    `;
        }).join('')}
                </tbody>
                <tfoot>
                    <tr>
                        <td class="personalization-modal--confirmation-product">Subtotal</td>
                        <td class="personalization-modal--confirmation-price">${window.formatMoney(subtotal)}</td>
                        <td class="personalization-modal--confirmation-actions"></td>
                    </tr>
                </tfoot>
            </table>
            ${termsHtml ?? ''}
        `;
    }
}

const STEP_CLASSES = [
    PersonalizationStepPlacement,
    PersonalizationStepType,
    PersonalizationStepDetails,
    PersonalizationStepConfirmation,
];

const STEP_TAGS = [
    'personalization-step-placement',
    'personalization-step-type',
    'personalization-step-details',
    'personalization-step-confirmation',
];

const STEP_NAMES = ['placement', 'type', 'details', 'confirmation'];

if (!customElements.get('personalization-modal')) {
    customElements.define('personalization-text-input', PersonalizationTextInput);
    customElements.define('personalization-icon-picker', PersonalizationIconPicker);
    customElements.define('personalization-font-picker', PersonalizationFontPicker);
    customElements.define('personalization-color-picker', PersonalizationColorPicker);
    customElements.define('personalization-image-picker', PersonalizationImagePicker);
    customElements.define('personalization-radio-group', PersonalizationRadioGroup);
    customElements.define('personalization-progress', PersonalizationProgress);
    customElements.define('personalization-preview-image', PersonalizationPreviewImage);
    customElements.define('personalization-step-actions', PersonalizationStepActions);
    customElements.define('personalization-cart', PersonalizationCart);
    customElements.define('personalization-step-placement', PersonalizationStepPlacement);
    customElements.define('personalization-step-type', PersonalizationStepType);
    customElements.define('personalization-step-details', PersonalizationStepDetails);
    customElements.define('personalization-step-confirmation', PersonalizationStepConfirmation);

    class PersonalizationModal extends PopupModal {
        #isRendering = false;
        #handleChangeBound = null;
        #updateCurrentStepBound = null;
        #handleSelectKeydownBound = null;
        #handleIconKeydownBound = null;
        #handlePersonalizationAddedBound = null;
        #handleImageCategoryChangeBound = null;
        #preserveSelectOpenOnRender = false;
        #preserveScrollOnRender = false;
        #scrollAnchorOnRender = null;
        static #SCROLL_PRESERVE_SELECTORS = [
            '.personalization-modal--main',
            '.personlization-modal--options-images',
        ];

        constructor() {
            super();

            this.confirmationHeading = this.getAttribute('confirmation-heading');
            this.confirmationDescription = this.getAttribute('confirmation-description');
        }

        connectedCallback() {
            this.elParentSection = this.closest('section') || document.body;
            this.elTriggers = this.elParentSection.querySelectorAll('[data-personalization-trigger]');
            this.elTriggers.forEach((el) => el.addEventListener('click', () => this.setAttribute('open', true)));

            this.elData = this.querySelector('[data-personalization-data]');
            this.data = JSON.parse(this.elData.textContent);

            this.state = new PersonalizationState(this.data);
            this.state.resetAll();

            const cartEdit = typeof window.expireStorageItem === 'function'
                ? window.expireStorageItem(PersonalizationCart.CART_EDIT_STORAGE_KEY)
                : null;
            let shouldOpenFromCartEdit = false;

            if (cartEdit?.variantId === this.data.base_product.id) {
                PersonalizationCart.hydrateStateFromCart(this.state, cartEdit);
                this.state.cartEditGroupKeys = cartEdit.groupKeys;
                this.state.cartEditQuantities = cartEdit.quantities ?? null;

                if (typeof window.storageAvailable === 'function' && window.storageAvailable()) {
                    localStorage.removeItem(PersonalizationCart.CART_EDIT_STORAGE_KEY);
                }

                shouldOpenFromCartEdit = true;
            } else {
                this.state.loadDraft();
            }

            this.focusElement = null;
            this.focusSelectionStart = null;
            this.focusSelectionEnd = null;

            this.elContent = this.querySelector('[data-template]');
            this.elTerms = this.querySelector('[data-terms]');

            this.#handleChangeBound = this.#handleChange.bind(this);
            this.#updateCurrentStepBound = this.#updateCurrentStep.bind(this);
            this.#handleSelectKeydownBound = this.#handleSelectKeydown.bind(this);
            this.#handleIconKeydownBound = this.#handleKeydown.bind(this);
            this.#handlePersonalizationAddedBound = this.#handlePersonalizationAdded.bind(this);
            this.#handleImageCategoryChangeBound = this.#handleImageCategoryChange.bind(this);

            this.elContent.addEventListener('change', this.#handleChangeBound);
            this.elContent.addEventListener('input', this.#handleChangeBound);
            this.elContent.addEventListener('click', this.#updateCurrentStepBound);
            this.elContent.addEventListener('keydown', this.#handleSelectKeydownBound, true);
            this.elContent.addEventListener('keydown', this.#handleIconKeydownBound, true);
            this.elContent.addEventListener('personalization-image-category-change', this.#handleImageCategoryChangeBound);
            this.addEventListener('personalization-added', this.#handlePersonalizationAddedBound);

            this.#render();

            if (shouldOpenFromCartEdit) {
                this.setAttribute('open', 'true');
            }
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (name === 'open' && newValue === 'true') {
                if (
                    !this.state.cartEditGroupKeys?.length &&
                    this.state.confirmedPersonalizations?.length &&
                    !this.state.hasAnyUnconfiguredPlacements() &&
                    !PersonalizationStepDetails.isDraftComplete(this.state)
                ) {
                    this.state.steps.current = this.state.steps.total;
                }

                this.#render();
            }

            super.attributeChangedCallback(name, oldValue, newValue);
        }

        close() {
            this.state.persistDraft();
            this.state.resetInProgress();
            super.close();
        }

        clearSavedDesigns() {
            this.state.clearDraft();
            this.state.resetAll();
            this.state.cartEditGroupKeys = null;

            if (this.getAttribute('open') === 'true') {
                this.#render();
            }
        }

        updateData(data) {
            if (this.data?.base_product?.id !== data.base_product.id) {
                this.state?.persistDraft();
            }

            this.data = data;

            if (this.elData) {
                this.elData.textContent = JSON.stringify(data);
            }

            this.state = new PersonalizationState(this.data);
            this.state.resetAll();
            this.state.loadDraft();

            if (this.getAttribute('open') === 'true') {
                this.#render();
            }
        }

        #getStepContext() {
            return {
                data: this.data,
                currentSelections: this.state.currentSelections,
                confirmedPersonalizations: this.state.confirmedPersonalizations,
                editingConfirmedIndex: this.state.editingConfirmedIndex,
                steps: this.state.steps,
                currentPersonalizationProduct: this.state.currentPersonalizationProduct,
                confirmationHeading: this.confirmationHeading,
                confirmationDescription: this.confirmationDescription,
                termsHtml: this.elTerms ? this.elTerms.innerHTML : '',
            };
        }

        #handlePersonalizationAdded() {
            const elStatus = this.querySelector('[data-personalization-status]');

            if (elStatus) {
                elStatus.textContent = 'Personalization added to cart.';
            }

            window.setTimeout(() => {
                this.state.clearDraft();
                this.state.resetAll();
                super.close();

                if (elStatus) {
                    elStatus.textContent = '';
                }
            }, 150);
        }

        #render() {
            this.#isRendering = true;

            const StepClass = STEP_CLASSES[this.state.steps.current - 1];
            StepClass.beforeRender?.(this.state);

            const renderState = this.#captureRenderState();
            this.elContent.innerHTML = this.#template();
            this.#restoreRenderState(renderState);

            this.focusElement = null;
            this.focusSelectionStart = null;
            this.focusSelectionEnd = null;
            this.#preserveSelectOpenOnRender = false;
            this.#preserveScrollOnRender = false;
            this.#scrollAnchorOnRender = null;

            this.#isRendering = false;
        }

        #getScrollContainers() {
            const containers = [];

            if (this.elContent) {
                containers.push({ selector: null, el: this.elContent });
            }

            for (const selector of PersonalizationModal.#SCROLL_PRESERVE_SELECTORS) {
                const el = this.elContent?.querySelector(selector);

                if (el) {
                    containers.push({ selector, el });
                }
            }

            return containers;
        }

        #findRadioGroupByFingerprint(fingerprint) {
            if (!fingerprint) {
                return null;
            }

            for (const el of this.elContent?.querySelectorAll('personalization-radio-group') ?? []) {
                const { step, options } = decodeAttr(el.getAttribute('context'));

                if (optionGroupFingerprint(step, options) === fingerprint) {
                    return el;
                }
            }

            return null;
        }

        #captureScrollAnchor(fingerprint) {
            const anchor = this.#findRadioGroupByFingerprint(fingerprint);

            if (!anchor) {
                return null;
            }

            const offsets = [];

            for (const { selector, el } of this.#getScrollContainers()) {
                if (!el.contains(anchor)) {
                    continue;
                }

                const scrollerRect = el.getBoundingClientRect();
                const anchorRect = anchor.getBoundingClientRect();

                offsets.push({
                    selector,
                    visualOffset: anchorRect.top - scrollerRect.top,
                });
            }

            if (!offsets.length) {
                return null;
            }

            return { fingerprint, offsets };
        }

        #restoreScrollAnchor(anchorState) {
            if (!anchorState?.offsets?.length) {
                return;
            }

            const restore = () => {
                const anchor = this.#findRadioGroupByFingerprint(anchorState.fingerprint);

                if (!anchor) {
                    return;
                }

                for (const { selector, visualOffset } of anchorState.offsets) {
                    const el = selector
                        ? this.elContent?.querySelector(selector)
                        : this.elContent;

                    if (!el?.contains(anchor)) {
                        continue;
                    }

                    const scrollerRect = el.getBoundingClientRect();
                    const anchorRect = anchor.getBoundingClientRect();
                    const currentVisualOffset = anchorRect.top - scrollerRect.top;
                    const delta = currentVisualOffset - visualOffset;

                    if (delta !== 0) {
                        el.scrollTop += delta;
                    }
                }
            };

            requestAnimationFrame(restore);
        }

        #captureScrollPositions() {
            const positions = [{
                selector: null,
                scrollTop: this.elContent?.scrollTop ?? 0,
                scrollLeft: this.elContent?.scrollLeft ?? 0,
            }];

            for (const selector of PersonalizationModal.#SCROLL_PRESERVE_SELECTORS) {
                const el = this.elContent?.querySelector(selector);

                if (!el) {
                    continue;
                }

                positions.push({
                    selector,
                    scrollTop: el.scrollTop,
                    scrollLeft: el.scrollLeft,
                });
            }

            return positions;
        }

        #restoreScrollPositions(positions) {
            if (!positions?.length) {
                return;
            }

            const restore = () => {
                for (const { selector, scrollTop, scrollLeft } of positions) {
                    const el = selector
                        ? this.elContent?.querySelector(selector)
                        : this.elContent;

                    if (!el) {
                        continue;
                    }

                    el.scrollTop = scrollTop;
                    el.scrollLeft = scrollLeft;
                }
            };

            requestAnimationFrame(restore);
        }

        #captureRenderState() {
            const openSelectToggleIds = this.#preserveSelectOpenOnRender
                ? [...this.querySelectorAll('custom-select[open="true"]')]
                    .map((el) => el.querySelector('[data-toggle]')?.id)
                    .filter(Boolean)
                : [];

            let focusId = this.focusElement;
            let selectionStart = this.focusSelectionStart;
            let selectionEnd = this.focusSelectionEnd;

            if (!focusId) {
                const elFocused = this.contains(document.activeElement) ? document.activeElement : null;

                if (elFocused?.id) {
                    focusId = elFocused.id;

                    if (typeof elFocused.selectionStart === 'number') {
                        selectionStart = elFocused.selectionStart;
                        selectionEnd = elFocused.selectionEnd;
                    }
                }
            }

            return {
                preserveSelectOpen: this.#preserveSelectOpenOnRender,
                preserveScroll: this.#preserveScrollOnRender,
                scrollPositions: this.#preserveScrollOnRender ? this.#captureScrollPositions() : null,
                scrollAnchor: this.#scrollAnchorOnRender,
                openSelectToggleIds,
                focusId,
                selectionStart,
                selectionEnd,
            };
        }

        #restoreRenderState({ preserveSelectOpen, preserveScroll, scrollPositions, scrollAnchor, openSelectToggleIds, focusId, selectionStart, selectionEnd }) {
            const toggleIdsToOpen = new Set(openSelectToggleIds);

            if (preserveSelectOpen && focusId) {
                const elFocus = this.querySelector(`#${CSS.escape(focusId)}`);
                const toggleId = elFocus?.closest('custom-select')?.querySelector('[data-toggle]')?.id;

                if (toggleId) {
                    toggleIdsToOpen.add(toggleId);
                }
            }

            for (const toggleId of toggleIdsToOpen) {
                const toggle = this.querySelector(`#${CSS.escape(toggleId)}`);
                toggle?.closest('custom-select')?.setAttribute('open', 'true');
            }

            if (focusId) {
                const newFocusElement = this.querySelector(`#${CSS.escape(focusId)}`);

                if (newFocusElement) {
                    newFocusElement.focus({ preventScroll: true });

                    if (
                        typeof selectionStart === 'number' &&
                        typeof newFocusElement.setSelectionRange === 'function'
                    ) {
                        newFocusElement.setSelectionRange(selectionStart, selectionEnd);
                    }
                }
            }

            if (preserveScroll) {
                this.#restoreScrollPositions(scrollPositions);
            } else if (scrollAnchor) {
                this.#restoreScrollAnchor(scrollAnchor);
            }
        }

        #handleSelectKeydown(ev) {
            if (!ev.target.closest('custom-select input[type="radio"]')) {
                return;
            }

            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(ev.key)) {
                this.#preserveSelectOpenOnRender = true;
            }
        }

        #handleKeydown(ev) {
            const StepClass = STEP_CLASSES[this.state.steps.current - 1];
            StepClass.handleKeydown?.(ev);
        }

        #handleImageCategoryChange(ev) {
            const { categoryIndex } = ev.detail ?? {};

            if (!Number.isFinite(categoryIndex)) {
                return;
            }

            this.state.currentSelections.imageCategoryIndex = categoryIndex;
        }

        #handleChange(ev) {
            if (this.#isRendering) {
                return;
            }

            const elTarget = ev.target;

            if (ev.type === 'input' && elTarget.matches('input[type="radio"], input[type="checkbox"]')) {
                return;
            }

            if (elTarget.matches('input[data-input="text"]')) {
                if (ev.type === 'change') {
                    return;
                }

                this.#applyTextInputSelection(elTarget);
                return;
            }

            this.#applySelection(elTarget);
        }

        #applyTextInputSelection(target) {
            const result = PersonalizationStepDetails.handleSelection(this.state, this.data, target);

            if (!result) {
                return;
            }

            const { step, valueObject, syncCart } = result;

            this.state.currentSelections[step] = valueObject;
            PersonalizationCart.syncFromState(this.state, syncCart);

            const sanitizedText = valueObject.text ?? '';

            if (target.value !== sanitizedText) {
                target.value = sanitizedText;

                if (result.sanitizedSelectionStart !== null && result.sanitizedSelectionStart !== undefined) {
                    target.setSelectionRange(result.sanitizedSelectionStart, result.sanitizedSelectionEnd);
                }
            }

            this.#patchTextDetailsUI();
        }

        #patchTextDetailsUI() {
            const properties = this.state.currentSelections.type?.properties;
            const details = this.state.currentSelections.details ?? {};

            this.querySelector('personalization-text-input')?.updateFromDetails(properties, details);
            this.querySelector('personalization-icon-picker')?.updateDisabledState(details, properties);

            const elPreview = this.querySelector('personalization-preview-image');

            if (elPreview) {
                PersonalizationPreviewImage.patchDraftOverlay(elPreview, this.state.currentSelections);
            }

            this.querySelector('personalization-step-actions')?.updateCanNext(
                PersonalizationStepDetails.isDraftComplete(this.state)
            );
        }

        #applySelection(target) {
            const StepClass = STEP_CLASSES[this.state.steps.current - 1];
            const result = StepClass.handleSelection(this.state, this.data, target);

            if (!result) {
                return;
            }

            const { step, valueObject, clearDownstream, currentPersonalizationProduct, syncCart, imageCategoryIndex } = result;

            if (clearDownstream) {
                for (const key of clearDownstream) {
                    this.state.currentSelections[key] = null;
                }

                if (clearDownstream.some((key) => ['details', 'type', 'placement'].includes(key))) {
                    delete this.state.currentSelections.imageCategoryIndex;
                }
            }

            if (currentPersonalizationProduct !== undefined) {
                this.state.currentPersonalizationProduct = currentPersonalizationProduct;
            }

            if (imageCategoryIndex !== undefined) {
                this.state.currentSelections.imageCategoryIndex = imageCategoryIndex;
            }

            const elCustomSelect = target.closest('custom-select');

            if (elCustomSelect && this.#preserveSelectOpenOnRender) {
                this.focusElement = target.id;
            } else if (elCustomSelect) {
                this.focusElement = elCustomSelect.querySelector('[data-toggle]')?.id ?? target.id;
            } else {
                this.focusElement = target.id;
            }

            if (result.sanitizedSelectionStart !== null && result.sanitizedSelectionStart !== undefined) {
                this.focusSelectionStart = result.sanitizedSelectionStart;
                this.focusSelectionEnd = result.sanitizedSelectionEnd;
            } else if (typeof target.selectionStart === 'number') {
                this.focusSelectionStart = target.selectionStart;
                this.focusSelectionEnd = target.selectionEnd;
            }

            this.state.currentSelections[step] = valueObject;
            PersonalizationCart.syncFromState(this.state, syncCart);
            this.#preserveScrollOnRender = true;
            this.#render();
        }

        #updateCurrentStep(ev) {
            const actionEl = ev.target.closest('[data-action]');

            if (!actionEl || this.#isRendering) {
                return;
            }

            const direction = actionEl.dataset.action;

            if (direction === 'edit' || direction === 'remove') {
                const StepClass = STEP_CLASSES[this.state.steps.current - 1];
                const handler =
                    direction === 'remove' ? StepClass.handleRemove : StepClass.handleAction;
                const result = handler?.(this.state, actionEl, {
                    persistDraft: () => this.state.persistDraft(),
                });

                if (result) {
                    if (result.syncCart) {
                        PersonalizationCart.syncFromState(this.state, result.syncCart);
                    }

                    this.#render();
                }

                return;
            }

            if (direction === 'save') {
                PersonalizationStepConfirmation.save(this.state, {
                    persistDraft: () => this.state.persistDraft(),
                    render: () => this.#render(),
                });
                return;
            }

            if (direction === 'add') {
                return;
            }

            if (direction === 'prev') {
                const currentStepIndex = this.state.steps.current - 1;
                const destinationStepIndex = currentStepIndex - 1;
                const sharedFingerprints = this.#getSharedStepFingerprints(currentStepIndex, destinationStepIndex);

                if (sharedFingerprints.length) {
                    this.#scrollAnchorOnRender = this.#captureScrollAnchor(sharedFingerprints[0]);
                }

                const currentStep = Object.keys(this.state.currentSelections)[this.state.steps.current];
                this.state.currentSelections[currentStep] = null;
                this.state.steps.current -= 1;
            } else if (direction === 'next') {
                const currentStepIndex = this.state.steps.current - 1;
                const destinationStepIndex = currentStepIndex + 1;
                const sharedFingerprints = this.#getSharedStepFingerprints(currentStepIndex, destinationStepIndex);

                if (sharedFingerprints.length) {
                    this.#scrollAnchorOnRender = this.#captureScrollAnchor(sharedFingerprints[0]);
                }

                this.state.steps.current += 1;

                if (
                    this.state.steps.current === this.state.steps.total &&
                    PersonalizationStepDetails.isDraftComplete(this.state)
                ) {
                    PersonalizationCart.syncFromState(this.state, { includeCurrentDraft: true });
                }
            }

            this.#render();
        }

        #getSharedStepFingerprints(currentStepIndex, destinationStepIndex) {
            if (destinationStepIndex < 0 || destinationStepIndex >= STEP_CLASSES.length) {
                return [];
            }

            const CurrentStepClass = STEP_CLASSES[currentStepIndex];
            const DestinationStepClass = STEP_CLASSES[destinationStepIndex];
            const currentFingerprints = CurrentStepClass.getOptionGroupFingerprints?.(this.state, this.data) ?? [];
            const destinationFingerprints = DestinationStepClass.getOptionGroupFingerprints?.(this.state, this.data) ?? [];

            return getSharedOptionGroupFingerprints(currentFingerprints, destinationFingerprints);
        }

        #template() {
            const stepIndex = this.state.steps.current - 1;
            const activeStep = STEP_NAMES[stepIndex];
            const stepTag = STEP_TAGS[stepIndex];
            const StepClass = STEP_CLASSES[stepIndex];
            const stepContext = encodeAttr(this.#getStepContext());

            return `
                <form class="personalization-modal--form" data-form>
                    <div class="personalization-modal--main">
                        <div class="personalization-modal--progress">
                            <personalization-progress context="${encodeAttr({
                current: this.state.steps.current,
                total: this.state.steps.total,
            })}"></personalization-progress>
                        </div>
                        <div class="personalization-modal--image">
                            <personalization-preview-image
                                selections="${encodeAttr(this.state.currentSelections)}"
                                confirmed-personalizations="${encodeAttr(this.state.confirmedPersonalizations)}"
                                editing-confirmed-index="${this.state.editingConfirmedIndex ?? ''}"
                                personalizations="${encodeAttr(this.data.personalizations)}"
                            ></personalization-preview-image>
                        </div>
                        <${stepTag} context="${stepContext}" class="personalization-modal--step ${activeStep} ${this.state.currentSelections.type?.type == 'image' ? 'image' : ''}"></${stepTag}>
                    </div>
                    <div class="personalization-modal--actions">
                        <personalization-step-actions context="${encodeAttr({
                current: this.state.steps.current,
                total: this.state.steps.total,
                canNext: StepClass.isComplete(this.state),
                showSave: PersonalizationStepConfirmation.showAddMoreDesigns(this.state),
            })}"></personalization-step-actions>
                        ${this.state.steps.current === this.state.steps.total ? `
                            <personalization-cart></personalization-cart>
                        ` : ''}
                    </div>
                </form>
            `;
        }

    }

    customElements.define('personalization-modal', PersonalizationModal);
}

window.startPersonalizationCartEdit = (payload) => PersonalizationCart.startCartEdit(payload);
window.clearPersonalizationDraft = (variantId) => PersonalizationCart.clearSavedDesigns(variantId);
